/**
 * Todo el juego trabaja en "unidades de mundo", no en pixeles.
 * El render escala el mundo para encajar en el canvas, asi la fisica
 * es identica en cualquier resolucion.
 *
 * Origen (0,0) = esquina superior izquierda del terreno de juego.
 */
export const FIELD = {
  WIDTH: 1600,
  HEIGHT: 1000,
  /** Margen exterior visible: fondo de porteria + aire alrededor. */
  MARGIN: 90,
  /** Alto de la boca de porteria. */
  GOAL_HEIGHT: 235,
  /** Profundidad de la red, hacia fuera del campo. */
  GOAL_DEPTH: 70,
  /**
   * Altura del larguero, en unidades de mundo. FIJA, no proporcional al ancho.
   *
   * Primer intento: 1/3 del ancho de la boca, que es la proporcion de una
   * porteria real. Fue un error, y `pnpm sim` lo caso: `goalScale` ENCOGE la
   * porteria en los modos grandes para compensar que hay mas jugadores (0,45
   * en 11v11), asi que el larguero bajaba a 35 — por debajo de donde entra la
   * gente. El 7v7 paso de 2,0 goles por partido a 0,2.
   *
   * La clave: el ancho de la boca se escala por EQUILIBRIO, pero la fisica de
   * altura (`LOFT_GRAVITY`, la potencia de golpeo) no escala con el modo. Un
   * techo que depende del ancho mezcla dos cosas que no tienen nada que ver.
   * Y la proporcion, en cenital, no se ve.
   *
   * 78 es la proporcion real aplicada a la porteria de referencia (235/3).
   * Medido con `pnpm crossbar:audit`: los goles cruzan a 38 en el p99, y con
   * el radio del balon mas el de la madera hace falta margen de 15, asi que 78
   * deja 25 unidades de aire sobre el disparo mas alto que entra hoy.
   */
  CROSSBAR_HEIGHT: 78,
  /** Area grande: ancho (hacia dentro) x alto. */
  BOX_WIDTH: 260,
  BOX_HEIGHT: 560,
  CENTER_CIRCLE_RADIUS: 150,
  LINE_WIDTH: 5,
  /** Tolerancia numerica al contar gol (u). Cero = solo entre postes. */
  GOAL_MOUTH_MARGIN: 0,
  /** Fraccion del radio que debe cruzar la linea para contar gol (1 = balon entero). */
  GOAL_LINE_CROSS: 1,
} as const;

export const PHYSICS = {
  /** Pasos de simulacion por segundo. Fijo e independiente del framerate. */
  TICK_RATE: 60,
  /** Duracion de un tick en segundos. */
  DT: 1 / 60,
  /** Tope de ticks por frame; evita la espiral de la muerte tras un stall. */
  MAX_TICKS_PER_FRAME: 5,
} as const;

export const PLAYER = {
  /** Fichas pequenas: 11v11 legible sobre el mismo campo. */
  RADIUS: 11,
  /** El portero sigue siendo algo mas grande dentro del area. */
  GK_RADIUS_SCALE: 1.28,
  /**
   * Aceleracion mientras se mantiene una direccion (u/s^2).
   *
   * Escalado a -15% (2600 -> 2210) junto con MAX_SPEED para que el ritmo se
   * vea mas de futbol real y menos de patinaje sobre hielo. La proporcion
   * ACCEL/FRICTION se mantiene igual (=MAX_SPEED), asi que no hace falta
   * retocar nada de giro/rozamiento aparte.
   */
  ACCEL: 2210,
  /** Rozamiento exponencial por segundo. Velocidad terminal = ACCEL / FRICTION. */
  FRICTION: 5,
  /**
   * Tope duro de velocidad (u/s). Bajado un 15% (520 -> 440) para que se
   * vea mas real. Las constantes de POSSESSION que usan velocidad ABSOLUTA
   * (no un ratio normalizado por MAX_SPEED) se han bajado en la misma
   * proporcion para no romper el equilibrio de conduccion por toques: ver
   * TOUCH_MIN_SPEED, TOUCH_SPEED_BASE, STEAL_REL_SPEED.
   */
  MAX_SPEED: 440,
  /**
   * Velocidad de giro de la mirada (rad/s). No es cosmetico: el balon se
   * conduce delante de donde miras, asi que si `facing` cambiase de golpe el
   * balon se teletransportaria y se perderia en cualquier giro. Girar cuesta
   * tiempo, y a maxima velocidad el toque va tan adelantado que el giro
   * empieza a costar tambien el balon.
   */
  TURN_RATE: 11,
  /** Con balon, el giro se ralentiza: no eres un tanque con ruedas. */
  TURN_WITH_BALL: 0.58,
  /** A maxima velocidad con balon, el giro baja aun mas. */
  TURN_AT_SPEED: 0.42,
  /** Rebote entre jugadores: 0 = se quedan pegados, 1 = choque elastico. */
  RESTITUTION: 0.35,
  /**
   * Impulso del dash (u/s). Supera MAX_SPEED a proposito; el rozamiento lo
   * va comiendo. Calibrado para desmarques cortos, no teletransporte.
   * Escalado -15% junto con MAX_SPEED (1380 -> 1175).
   */
  DASH_SPEED: 1175,
  /** Segundos entre dashes. */
  DASH_COOLDOWN: 0.9,
  /**
   * Entrada (K sin balon): mini-dash hacia el balon. Mas corto que el dash
   * general pero suficiente para superar STEAL_REL_SPEED y descolgar.
   * Escalado -15% junto con MAX_SPEED (920 -> 780).
   */
  TACKLE_SPEED: 780,
  /** Distancia maxima al balon para intentar entrar. */
  TACKLE_RANGE: 130,
  /** Segundos entre entradas. */
  TACKLE_COOLDOWN: 0.75,
} as const;

/**
 * Fatiga. El minuto 88 tiene que sentirse distinto del minuto 2: quien ha
 * esprintado mucho pierde punta de velocidad, y quien ha trotado la recupera.
 * No toca ACCEL: un jugador cansado sigue arrancando igual de rapido, solo
 * llega mas bajo. Es deliberado — la fatiga castiga el sprint sostenido, no
 * el primer paso.
 */
export const STAMINA = {
  /**
   * Fraccion de MAX_SPEED (sin fatigar) por encima de la cual se considera
   * "esprintando" y la resistencia empieza a bajar. Trotar por debajo de esto
   * no gasta nada, igual que en la vida real no te cansas caminando.
   */
  DRAIN_THRESHOLD: 0.82,
  /** Resistencia perdida por segundo mientras se esprinta (0-1 por segundo). */
  DRAIN_RATE: 0.11,
  /** Resistencia recuperada por segundo mientras no se esprinta. */
  RECOVER_RATE: 0.16,
  /**
   * Velocidad maxima con resistencia en 0, como fraccion de MAX_SPEED. No
   * baja mas: un jugador exhausto sigue siendo un futbolista, no se para en
   * seco. -22% es notable sin ser injugable.
   */
  MIN_SPEED_SCALE: 0.78,
} as const;

/**
 * Desequilibrio tras un golpe fuerte: entrada recibida, choque de cuerpo o
 * derribo del duelo. Un instante de peor control, no un tropiezo comico. Vive
 * separado de STAMINA porque es un evento puntual (choque), no un desgaste
 * acumulado (esfuerzo).
 */
export const IMPACT = {
  /**
   * Velocidad relativa de choque a partir de la cual se considera un golpe
   * fuerte y no un simple roce. Mismo umbral que POSSESSION.STEAL_REL_SPEED
   * porque es la misma nocion de "impacto real" medida en el mismo sitio.
   */
  STUMBLE_REL_SPEED: 170,
  /** Segundos de desequilibrio tras un golpe fuerte. */
  STUMBLE_TIME: 0.35,
  /** Aceleracion y giro se reducen a esta fraccion mientras dura. */
  STUMBLE_ACCEL_SCALE: 0.45,
  /** Distancia a la linea de fondo para el "pum" de choque en banda. */
  SIDELINE_BAND: 42,
  /** Velocidad relativa minima para sacudir la camara en banda. */
  SIDELINE_BUMP_MIN_SPEED: 115,
} as const;

export const BALL = {
  RADIUS: 6,
  /** Rozamiento exponencial por segundo (cesped). Alcance aproximado = velocidad / FRICTION. */
  FRICTION: 0.85,
  /**
   * Resistencia a rodadura: deceleracion CONSTANTE (u/s^2). DESACTIVADA (0).
   *
   * El modelo esta implementado y medido en `stepBall`, pero activarlo rompe el
   * juego y conviene saber por que antes de volver a intentarlo.
   *
   * Motivacion: el rozamiento exponencial puro (`v *= e^-kt`) nunca llega a
   * cero, asi que el balon se acerca asintoticamente a parado y deja una cola
   * perezosa de varios segundos deslizandose. Rodar sobre hierba es sobre todo
   * un termino constante. Con FRICTION 0.74 + ROLL_DECEL 25 el balon SI para
   * (1.6 s a 80 u/s, 2.6 s a 200) conservando los alcances de pase (+0.9%) y
   * disparo (+6%).
   *
   * Por que esta a 0: los alcances CORTOS caen un 8-23%, y la IA calcula la
   * potencia de pase con la formula del exponencial puro (`passTravelTime` en
   * ai/passPhysics.ts). Sus pases cortos se quedan cortos, la construccion se
   * rompe y hay semillas de 7v7 y 11v11 que pasan 60 s sin un solo disparo
   * (medido). Retocar k y ROLL_DECEL no lo arregla de forma fiable: unas
   * semillas se recuperan y otras caen.
   *
   * Para activarlo hay que arreglar antes el modelo de pase de la IA. El
   * alcance real tiene forma cerrada:
   *     D(v0) = v0/k - (a0/k^2)·ln(1 + k·v0/a0)
   * monotona en v0, asi que la potencia para una distancia se despeja con unas
   * pocas iteraciones de biseccion, y sigue siendo determinista.
   */
  ROLL_DECEL: 0,
  /** Por debajo de esto el balon se considera parado (evita deriva de milesimas). */
  STOP_SPEED: 1,
  /**
   * Constante equivalente para PREDECIR (IA, portero, iman de pase).
   *
   * Esas rutas estiman con la formula del exponencial puro (alcance = v/k), que
   * ya no describe el frenado real. Medido sobre el modelo nuevo, la k que
   * reproduce el alcance en el rango que importa (pase 940 → disparo 1.883) es
   * ~0.85, o sea el valor que tenia FRICTION antes. Se separa en su propia
   * constante para que quede explicito que es una aproximacion para predecir, y
   * que tocar el frenado real no descuadre en silencio a la IA.
   */
  FRICTION_EFFECTIVE: 0.85,
  /**
   * Resistencia del aire cuadratica (1/u). Solo en vuelo (loft alto): frena
   * misiles y vaselinas sin tocar la prediccion de pases a ras de suelo.
   */
  AIR_DRAG: 0.00022,
  MAX_SPEED: 2400,
  WALL_RESTITUTION: 0.72,
  /** Rebote del poste: vivo, pero sin convertir el palo en trampolin infinito. */
  POST_RESTITUTION: 0.82,
  /** Radio del poste (colisionador circular en cada esquina de la boca). */
  POST_RADIUS: 9,
  /** Holgura extra al separar el balon del palo (evita re-penetracion). */
  POST_SEPARATION: 1.5,
  /** Velocidad minima de alejamiento tras el rebote (u/s). */
  POST_MIN_OUTWARD: 160,
  /** Empuje extra hacia el campo tras pegarle a la madera. */
  POST_FIELD_KICK: 110,
  /** Impulso extra tras el rebote del palo (fraccion suave). */
  POST_SPEED_BOOST: 0.04,
  /**
   * Caida extra tras pegarle al larguero (u/s hacia abajo).
   *
   * Sin esto, un balon que toca la madera justo en su punto mas alto tiene
   * `loftVel` casi cero y se queda flotando en el larguero en vez de caer.
   */
  CROSSBAR_DROP: 70,
  /** Velocidad minima para el feedback visual/sonoro del palo (u/s). */
  POST_MIN_SPEED: 90,
  /** Rebote al chocar contra un jugador que no lo lleva. */
  PLAYER_RESTITUTION: 0.62,
  /** Cuanto se apaga el efecto por segundo. La comba se agota antes que el balon. */
  SPIN_DECAY: 1.5,
  /**
   * Magnus vertical: aceleracion vertical por unidad de (topSpin x velocidad).
   *
   * Un balon liftado BAJA antes de lo que dicta la parabola y uno cortado
   * flota. Sin esto no existe el disparo que se mete bajo el larguero ni la
   * vaselina que muerde. Calibrado contra LOFT_GRAVITY: a topSpin 0.85 (disparo
   * cargado a tope) y 1.500 u/s son ~245 u/s^2, o sea la mitad de la gravedad.
   * Se ve claramente sin convertir el vuelo en un truco de magia.
   */
  MAGNUS_VERT: 0.0004,
  /** El efecto vertical se agota algo mas despacio que la comba lateral. */
  TOPSPIN_DECAY: 1.1,
  /**
   * Agarre del bote: fraccion de la velocidad horizontal que el efecto añade
   * (liftado) o resta (cortado) al tocar cesped. Con 0.55 y topSpin -1 un
   * balon muy cortado se queda practicamente clavado en el sitio.
   */
  BOUNCE_GRIP: 0.55,
  /** Cuanto sube o baja la restitucion vertical el efecto. Cortado bota mas. */
  BOUNCE_SPIN_LIFT: 0.45,
  /** Fraccion de efecto que sobrevive a un bote (el cesped se lo come). */
  BOUNCE_SPIN_KEEP: 0.45,
  /** Desvio lateral que el efecto de guiñada imprime al botar. */
  BOUNCE_SIDE_KICK: 0.12,
  /** Gravedad vertical del balon en vuelo (unidades/s²). Solo altura visual. */
  LOFT_GRAVITY: 480,
  /** Restitucion vertical al botar en el cesped (0 = muerto, 1 = elastico). */
  LOFT_BOUNCE: 0.32,
  /** Por debajo de esta |loftVel| al tocar suelo, el bote se apaga. */
  LOFT_BOUNCE_MIN: 55,
  /** Por encima de esta altura no choca con jugadores de campo (pasa por encima). */
  LOFT_CLEAR_PLAYERS: 30,
  /** Control a ras de suelo. */
  LOFT_GROUND_CAPTURE: 14,
  /** Altura maxima para controlar un balon en el aire al bajar. */
  LOFT_CAPTURE_REACH: 44,
} as const;

/**
 * Dos formas de llevar el balon, y elegir entre ellas es la decision constante
 * del jugador:
 *
 *  - **Toque** (por defecto): al correr, el balon se golpea hacia delante cada
 *    vez que vuelve a los pies. Se pega y se va, como una carrera de verdad.
 *    Rapido, pero el balon pasa ratos lejos y es robable.
 *  - **Control cerrado** (Shift): el balon va cosido al pie con un muelle.
 *    Mucho mas dificil de quitar y coloca donde quieres, a cambio de correr
 *    bastante mas lento.
 */
export const POSSESSION = {
  /** Holgura sobre (radio jugador + radio balon) para capturar. */
  CAPTURE_PAD: 5,
  /** Holgura del punto de conduccion, parado. */
  CARRY_BASE_PAD: 2,
  /** Cuanto se adelanta el balon a maxima velocidad. Toque largo al esprintar. */
  CARRY_SPEED_EXTRA: 32,
  /** Fuerza del muelle que lleva el balon a su sitio (1/s). */
  CORRECTION_STIFFNESS: 16,
  /** Tope de la correccion (u/s), para que un balon lejano no salga disparado. */
  CORRECTION_MAX: 750,
  /**
   * Aceleracion maxima con la que el balon persigue su punto de conduccion
   * (u/s^2). Es LA constante que decide el dribbling: el balon conserva su
   * inercia, asi que al cortar de golpe sigue su linea y se queda atras. Muy
   * alta y el balon va pegado como un iman; muy baja y no hay forma de conducir.
   */
  CARRY_ACCEL: 7000,
  /**
   * Radio de control: si el balon se aleja mas que esto *del jugador*, se
   * suelta. Medirlo desde el jugador y no desde el punto de conduccion evita
   * que capturar o robar falle nada mas empezar (al capturar, el balon esta a
   * los pies, no en el punto de conduccion).
   *
   * Ademas da gratis el riesgo por velocidad: el toque ya se adelanta hasta
   * ~111 al esprintar, asi que quedan 39 de margen lanzado frente a 94 parado.
   */
  CONTROL_RADIUS: 62,
  /**
   * Por debajo de esta velocidad no se golpea el balon, se sujeta con el
   * muelle. Si no, quieto sobre el sitio uno se lo iria empujando solo.
   * Escalado -15% junto con MAX_SPEED (70 -> 60).
   */
  TOUCH_MIN_SPEED: 60,
  /**
   * Velocidad del toque = velocidad del jugador * GAIN + BASE. Calibrado para
   * que el balon se adelante y luego el rozamiento lo frene justo a tiempo de
   * alcanzarlo: ese vaiven es la cadencia de zancada.
   */
  TOUCH_SPEED_GAIN: 1.12,
  /** Escalado -15% junto con MAX_SPEED (140 -> 120): es un termino absoluto,
   *  no un ratio, asi que si no se escala aqui el toque deja de sentirse
   *  proporcional a la carrera al bajar la velocidad maxima. */
  TOUCH_SPEED_BASE: 120,
  /** Se golpea cuando el balon vuelve a esta distancia del jugador. */
  TOUCH_REACH_PAD: 4,
  /** Descuento de velocidad punta mientras se lleva el control cerrado. */
  CLOSE_CONTROL_SPEED: 0.62,
  /** Con el control cerrado el balon apenas se adelanta: va al pie. */
  CLOSE_CONTROL_CARRY_SCALE: 0.28,
  /** Cuanto mas cuesta robarle a quien lleva el control cerrado. */
  CLOSE_CONTROL_STEAL_RESISTANCE: 1.9,
  /** Fraccion de la velocidad del que entra que se lleva el balon al descolgarlo. */
  KNOCK_TRANSFER: 0.55,
  /** Bloqueo para recapturar tras golpear o perder el balon. */
  RECAPTURE_LOCKOUT: 0.28,
  /**
   * Velocidad relativa minima de un rival para robar por contacto (u/s).
   * Escalado -15% junto con MAX_SPEED (200 -> 170): compara velocidades de
   * jugador, no del balon, asi que tiene que bajar con ellas.
   */
  STEAL_REL_SPEED: 170,
  /**
   * Un rival pegado al cuerpo este tiempo seguido te quita el balon, aunque
   * ninguno de los dos se mueva. Es el duelo: aguantar de espaldas solo compra
   * tiempo, no es una fortaleza.
   *
   * Sin esto el juego se bloqueaba: atacante y defensor acababan pegados y
   * quietos, y como el robo exigia velocidad relativa, nadie ganaba nunca el
   * balon. Partidos enteros con un equipo monopolizando y cero goles.
   */
  DUEL_TIME: 0.6,
  /** Al perder el duelo el balon sale despedido lejos del que ha entrado. */
  DUEL_KNOCK_SPEED: 380,
  /**
   * Un rival intercepta el balon si lo tiene mas cerca que quien lo conduce.
   * Este es el precio real de correr: el toque se va tan por delante que deja
   * de estar protegido por el cuerpo. Sin velocidad minima: basta con estar ahi.
   */
  INTERCEPT_PAD: 4,
  /**
   * Velocidad relativa balon-jugador a partir de la cual el control es un
   * primer toque (hay que asentar antes de golpear).
   */
  FIRST_TOUCH_SPEED: 260,
  /** Cuanto se absorbe de la velocidad del balon al atraparlo con Shift. */
  FIRST_TOUCH_ABSORB: 0.62,
  /**
   * Cuanto se absorbe si el balon llega caliente y NO se esta pulsando Shift.
   * Para recibir con limpieza un pase/tiro de verdad hay que anticipar y
   * pulsar Shift; sin eso el balon apenas se frena y se escapa del control,
   * como un control de pecho mal preparado en la vida real. Por debajo de
   * FIRST_TOUCH_SPEED (balon lento/suelto) esto no aplica: no hace falta
   * prepararse para recibir algo que casi no se mueve.
   */
  FIRST_TOUCH_ABSORB_LOOSE: 0.12,
  /** Segundos sin poder golpear tras un primer toque exigente. */
  TRAP_TIME: 0.16,
  /** Desviacion maxima del primer toque bajo presion (fraccion de velocidad). */
  FIRST_TOUCH_ERROR_MAX: 0.22,
  /** Presion a partir de la cual crece el error de control. */
  FIRST_TOUCH_PRESSURE: 95,
  /**
   * Recepcion orientada: con Shift + direccion, el primer toque empuja el
   * balon hacia el stick (abrir carril / escapar) en vez de pegarlo al pie.
   */
  ORIENT_TRAP_PUSH: 165,
  ORIENT_TRAP_KEEP: 0.58,
  /**
   * Velocidad del toque al soltar Shift estando parado o casi ("mini pase").
   * Deliberadamente mas fuerte que TOUCH_SPEED_BASE (que es el suelo del toque
   * en carrera): soltar Shift es un gesto activo, tiene que notarse.
   */
  RELEASE_NUDGE_SPEED: 240,
  /**
   * Segundos tras soltar Shift en los que el toque se activa aunque el
   * jugador este parado (en vez de exigir TOUCH_MIN_SPEED). Es la ventana en
   * la que el "mini pase" empuja el balon fuera del alcance sin que el muelle
   * de conduccion lo reabsorba de inmediato.
   */
  RELEASE_GRACE_TIME: 0.3,
  /**
   * Si miras de espaldas al rival en el duelo, aguantas mas: es el escudo
   * de cuerpo. Mirar de cara al balon y de espalda a la presion.
   */
  SHIELD_DUEL_SCALE: 1.45,
  /**
   * Escudo de cuerpo contra la INTERCEPTACION (`findDisruption`, rama `take`),
   * el mecanismo que se lleva el 55-65% de las perdidas.
   *
   * `SHIELD_TAKE_DOT` es el coseno por debajo del cual se considera que el
   * dueno esta entre el rival y el balon (negativo = lados opuestos).
   * `SHIELD_TAKE_MARGIN` es lo que se le exige al ladron: cubriendo, no le
   * vale estar un pelo mas cerca del balon, tiene que estar claramente mas
   * cerca.
   *
   * Va sobre el margen y no sobre el alcance porque el alcance es casi todo
   * radios: `INTERCEPT_PAD` son 4 unidades de ~21, asi que la "resistencia"
   * apenas movia nada. Aplica igual al humano y a la IA.
   */
  SHIELD_TAKE_DOT: -0.25,
  SHIELD_TAKE_MARGIN: 0.7,
} as const;

/**
 * Golpeo de PRIMERA: rematar o pasar el balon que llega sin controlarlo antes.
 *
 * Todo el diseño gira alrededor del TIMING. La ventana es corta y el punto
 * dulce esta ligeramente ANTES del contacto, porque en el futbol de verdad el
 * golpeo se arma antes de que el balon llegue, no cuando ya lo tienes encima.
 * Un contacto perfecto pega mas fuerte que un disparo cargado; uno malo sale
 * escupido — pero SIEMPRE golpea, porque quedarse con la pierna en el aire se
 * lee como que el juego no ha respondido, y eso no se perdona.
 */
export const FIRST_TIME = {
  /** Ventana total antes del contacto en la que la tecla cuenta como primera (s). */
  WINDOW: 0.3,
  /** Momento ideal para pulsar, en segundos antes del contacto. */
  SWEET_SPOT: 0.08,
  /**
   * Margen (s) alrededor del punto dulce en el que la calidad cae de 1 a 0.
   *
   * NO es la ventana entera. Con la ventana (0.3 s) de denominador, pulsar en
   * el peor instante posible seguia dando 0.73 de calidad: el timing no
   * importaba y la mecanica era un boton de "disparo gratis". Con 0.12, pulsar
   * con el balon ya encima da ~0.33 (flojo) y solo el instante bueno saca el
   * golpeo limpio, que es de lo que iba todo esto.
   */
  TOLERANCE: 0.12,
  /** Alcance extra sobre el cuerpo para ABRIR la ventana (u). */
  REACH: 16,
  /**
   * Holgura sobre el cuerpo a la que el gesto armado GOLPEA de verdad.
   *
   * `REACH` decide cuando puedes pulsar; esto decide donde conecta. Son cosas
   * distintas y confundirlas era el fallo: el golpeo salia en el tick de la
   * pulsacion, o sea hasta a 213 unidades. Se pone algo por encima de la
   * captura (`POSSESSION.CAPTURE_PAD`, 22 en total) porque al ir a rematar se
   * estira mas la pierna que al controlar, y porque si el gesto llegara DESPUES
   * de la captura nunca saldria: habrias controlado el balon.
   */
  CONTACT_PAD: 10,
  /**
   * Cuanto dura el gesto armado antes de quedarse en nada (s).
   *
   * Cubre la ventana entera (0,3) con margen. Si el balon no llega, se pega al
   * aire — y eso es bueno: es el riesgo que la version instantanea no tenia.
   *
   * Probado a 0,55: los gestos fallados se quedan EXACTAMENTE igual (58 de 401),
   * o sea que no se falla por falta de tiempo sino porque el balon acaba en
   * otro sitio. Alargarlo solo compromete mas al jugador a cambio de nada.
   */
  SWING_TIME: 0.42,
  /** Velocidad relativa minima para considerar que el balon "llega" (u/s). */
  MIN_CLOSING_SPEED: 150,
  /**
   * Cuanto tiene que venir hacia ti (coseno). Con 0.35 vale un balon que cruza
   * en diagonal pero no uno que pasa de largo por delante.
   */
  MIN_AIM: 0.35,
  /** Por encima de esta altura, el golpeo sale de volea. */
  VOLLEY_HEIGHT: 16,
  /** Potencia con el peor timing posible dentro de la ventana. */
  POWER_MIN: 0.38,
  /** Potencia con contacto perfecto: por encima de un disparo cargado normal. */
  POWER_MAX: 1.12,
  /** Dispersion con contacto perfecto (casi nula: el premio es precision). */
  SPREAD_PERFECT: 0.15,
  /** Dispersion con el peor contacto: el balon se va a cualquier parte. */
  SPREAD_POOR: 2.4,
  /** Umbrales de calidad para el feedback. */
  PERFECT_AT: 0.82,
  GOOD_AT: 0.5,
  /** Bloqueo de recaptura del que remata, para que no se lo devuelva a si mismo. */
  RECAPTURE_LOCKOUT: 0.28,
} as const;

export const KICK = {
  /** Pase base. El centro a tope suma PASS_LONG_BONUS + loft alto. */
  PASS_SPEED: 940,
  SHOOT_SPEED: 1760,
  /** Fraccion de la velocidad del jugador que hereda el balon al disparar. */
  INHERIT: 0.28,
  /** Herencia al disparar con mira: la direccion manda, no la carrera. */
  AIMED_INHERIT: 0.1,
  /** Herencia mas baja en pase: no regalar velocidad al rival en mal pase. */
  PASS_INHERIT: 0.2,
  /** Tiempo cargando para potencia maxima de disparo. */
  CHARGE_TIME: 0.42,
  /** Los pases cargan mas rapido: son un gesto, no un wind-up. */
  PASS_CHARGE_TIME: 0.34,
  /**
   * Carga por debajo de la cual el golpeo sale como PASE. Por encima, disparo.
   *
   * Es toda la mecanica de golpeo en un numero. 0,42 sobre una carga completa
   * de 0,42 s son ~0,18 s de boton: un toque seco pasa, mantener dispara, y en
   * el limite tienes el pase fuerte y el disparo colocado — que es justo donde
   * se juega. Nada adivina tu intencion; decide el dedo.
   */
  PASS_MAX_CHARGE: 0.42,
  /** Potencia minima de disparo (toque seco). */
  MIN_POWER: 0.48,
  PASS_MIN_POWER: 0.38,
  /**
   * A partir de esta carga el pase se convierte en centro / cambio de juego:
   * mas velocidad horizontal y mas altura que un disparo potente.
   */
  PASS_LONG_CHARGE: 0.78,
  /** Bonus de velocidad en pase a tope (centro largo). */
  PASS_LONG_BONUS: 1.32,
  /** Herencia al pasar con mira cargada. */
  PASS_AIMED_INHERIT: 0.06,
  /** Comba en pase con mira activa. */
  AIMED_PASS_SPIN_SCALE: 0.1,
  /** Distancias objetivo del iman de pase segun carga (u). */
  PASS_ASSIST_DIST_MIN: 90,
  PASS_ASSIST_DIST_SHORT: 230,
  PASS_ASSIST_DIST_MID: 520,
  PASS_ASSIST_DIST_LONG: 980,
  /** Carga a partir de la cual entra bonus de "tiro perfecto". */
  PERFECT_CHARGE: 0.88,
  /** Bonus de velocidad en tiro perfecto con mira. */
  PERFECT_CHARGE_BONUS: 1.12,
  /** Comba en tiro con mira activa (casi recto hacia la mira). */
  AIMED_SPIN_SCALE: 0.08,
  /**
   * Efecto VERTICAL por gesto (ver `resolveKickTopSpin`). Positivo = liftado
   * (baja en vuelo, sale disparado al botar); negativo = cortado.
   */
  TOPSPIN_SHOT: 0.85,
  /** Carga minima de disparo para empezar a liftar: un toque seco no dobla. */
  TOPSPIN_MIN_CHARGE: 0.55,
  TOPSPIN_VOLLEY: 0.5,
  TOPSPIN_CHIP: 1.1,
  TOPSPIN_CROSS: 0.35,
  /**
   * Efecto en disparo. La comba maxima es aproximadamente SPIN_GAIN / SPIN_DECAY
   * radianes cuando corres a tope y disparas cruzado.
   */
  /**
   * Comba por gesto de mira. Es la mecanica de disparo, no un detalle:
   * mientras cargas, la velocidad a la que giras la mira en el ultimo momento
   * se convierte en efecto. Abres el pie hacia dentro y el balon entra
   * rosqueado; lo cierras y sale a la escuadra contraria.
   *
   * El balon sale por la LINEA de mira y se comba desde ahi. O sea: la linea
   * deja de ser una promesa de donde acaba y pasa a ser por donde arranca —
   * que es justo lo que separa apuntar de tirar.
   */
  /**
   * Barrido (rad) por debajo del cual no comba nada.
   *
   * Eran 3 grados y se quedaban cortos: apuntar de una diagonal a la de al
   * lado son 45°, asi que el ajuste fino de punteria —el que solo quiere mover
   * la flecha dos dedos— entraba en zona de comba y el balon salia rosqueado
   * sin que nadie lo hubiera pedido. Con 4 grados dirigir y rosquear vuelven a
   * ser dos gestos distintos.
   */
  SWEEP_DEADZONE: 0.07,
  /**
   * Barrido (rad) a partir del cual la comba ya es la maxima.
   *
   * Eran 49 grados y era demasiado pedir: para rosquear del todo habia que
   * girar la mira medio cuadrante FUERA de donde querias tirar, y a mitad de
   * gesto ya no sabias a donde apuntabas. Con 29 grados el efecto pleno cabe
   * dentro de un ajuste que todavia se lee como apuntar.
   */
  SWEEP_FULL: 0.5,
  /** Efecto a comba maxima. */
  SWEEP_SPIN: 0.95,
  /**
   * Un tiro colocado envuelve el balon y comba mas; un cañonazo lo golpea de
   * pleno y comba menos. Es lo que da profundidad a la mecanica: para rosquear
   * de verdad hay que renunciar a potencia.
   */
  SWEEP_FINESSE_SCALE: 1.3,
  SWEEP_POWER_SCALE: 0.82,
  /** El pase tambien comba con el gesto, pero mucho menos que el tiro. */
  SWEEP_PASS_SCALE: 0.45,
  /**
   * AFTERTOUCH (Sensible Soccer): durante `AFTERTOUCH_TIME` segundos despues
   * de un disparo, mantener una direccion que se aparte del vuelo del balon
   * (mas de `AFTERTOUCH_DEADZONE` de lado) lo dobla hacia ahi, `AFTERTOUCH_RATE`
   * de efecto por segundo hasta `AFTERTOUCH_MAX` en total. Se acaba antes si
   * el balon llega a alguien, alguien lo controla o baja de
   * `AFTERTOUCH_MIN_SPEED`.
   *
   * Existe por Discord (2026-09-29, "el angulo del tiro va oxidado"): con
   * teclado el golpeo sale en ocho direcciones y cualquier ajuste de la mira
   * se convierte en comba (`SWEEP_*`), asi que no habia forma de colocar un
   * tiro a un palo. Con esto se apunta en ocho y se coloca despues, que es
   * como se juega con teclado desde los 90. Va por el `InputFrame`, asi que
   * es igual en todas las maquinas. 0 en `AFTERTOUCH_TIME` = como antes.
   *
   * Calibrado a 600 u (la porteria de 3v3 mide 259): mantenido hasta el tope
   * (~0,2 s) desvia 90 u un tiro colocado y 48 un cañonazo en 3v3 (121 y 84
   * en 11v11); un toque de 0,1 s, 31-69. La mitad que la comba del gesto: es
   * para colocar a un palo, no para hacer un bumeran.
   */
  AFTERTOUCH_TIME: 0.35,
  AFTERTOUCH_RATE: 3.2,
  AFTERTOUCH_MAX: 0.7,
  AFTERTOUCH_DEADZONE: 0.35,
  AFTERTOUCH_MIN_SPEED: 250,
  SPIN_GAIN: 0.64,
  PASS_SPIN_GAIN: 0.22,
  /** Dispersion maxima sin apuntar, parado / esprintando (rad). */
  SPREAD_STAND: 0.022,
  SPREAD_SPRINT: 0.105,
  PASS_SPREAD_SCALE: 0.72,
  CLOSE_CONTROL_SPREAD_SCALE: 0.48,
  CLOSE_CONTROL_SHOT_SCALE: 0.92,
  /** Error angular extra a stamina 0 (rad). */
  FATIGUE_SPREAD_GAIN: 0.085,
  /** Error angular extra bajo presion fisica (rad). */
  PRESSURE_SPREAD_GAIN: 0.07,
  /** Escala de error de remate por habilidad de colocacion. */
  SHOT_ERROR_BASE: 0.018,
  SHOT_ERROR_DIST_SCALE: 900,
  /**
   * Penaliza golpear cruzado respecto a la carrera. Solo afecta al humano
   * (la IA golpea con `directionLocked`).
   *
   * Dos reglas que antes no se cumplian y hacian el golpeo impreciso hasta lo
   * injugable — medido: pedir un pase a 90° estando PARADO salia 32° desviado:
   *
   *  1. **Escala con la velocidad**: parado puedes plantar el pie y golpear a
   *     donde quieras. El castigo es de correr cruzado, no de girar el cuerpo.
   *  2. **Satura** (`sin(a/2)`): antes crecia lineal con el angulo, asi que un
   *     pase hacia atras acumulaba 68° de error el solo.
   *
   * Habia una tercera —apuntar con Espacio dejaba el castigo al 15%— pero se
   * fue con la tecla. Ahora el castigo es siempre el pleno, y por eso importa
   * que las dos reglas de arriba lo mantengan acotado.
   */
  MISALIGN_GAIN: 0.2,
  /** Por debajo de esta carga con mira = colocada (menos potencia, mas control). */
  FINESSE_CHARGE: 0.38,
  FINESSE_SPEED_MIN: 0.72,
  FINESSE_SPEED_MAX: 0.9,
  FINESSE_SPIN_SCALE: 0.35,
  /** Efecto en tiro parado con mira: curva deliberada (rad/s aprox). */
  FINESSE_STAND_SPIN: 0.42,
  /** Multiplicador de comba en tiros potentes en carrera. */
  POWER_SHOT_SPIN_SCALE: 1.15,
  /** Vaselina: loft alto, poca potencia horizontal. */
  CHIP_LOFT: 340,
  CHIP_SPEED_SCALE: 0.58,
  /** Remate picado / volea: balon contra el cesped. */
  VOLLEY_LOFT: 48,
  VOLLEY_SPEED_SCALE: 1.06,
  /** Velocidad vertical inicial en tiros (altura ~ v²/2g). */
  LOFT_SHOOT_MIN: 95,
  LOFT_SHOOT_MAX: 210,
  LOFT_CROSS: 165,
  /**
   * Pase raso / elevado / cambio de juego (velocidad vertical inicial).
   * A tope (SWITCH) va mas alto que un disparo fuerte.
   */
  LOFT_PASS_MIN: 0,
  LOFT_PASS_MID: 58,
  LOFT_PASS_LONG: 150,
  LOFT_PASS_SWITCH: 250,
  /** Colocada: menos elevacion. */
  FINESSE_LOFT_SCALE: 0.48,
  /**
   * Centro desde banda. Mas rapido que un pase, con comba hacia el area;
   * el timing lo marca el receptor llegando al punto de caida.
   */
  CROSS_SPEED: 1180,
  CROSS_INHERIT: 0.16,
  CROSS_SPIN_GAIN: 0.78,
  CROSS_MIN_POWER: 0.48,
  CROSS_CHARGE_TIME: 0.36,
  /** Distancia a la banda para considerar que estas en carril de centro. */
  CROSS_WING_BAND: 175,
  /** Fraccion del campo: hay que estar en el ultimo tramo atacante. */
  CROSS_ATTACK_DEPTH: 0.52,
  /** Semianchura del area de caida de un centro, alrededor del eje. */
  CROSS_BOX_HALF_WIDTH: 210,
} as const;

/**
 * Remate imparable tras recibir un centro en el area rival bajo presion.
 * Disparar en el punto dulce clava el balon al palo; el portero no llega.
 */
export const CROSS_FINISH = {
  /** Ventana total tras controlar el centro en el area. */
  WINDOW: 0.58,
  /** Inicio del punto dulce (s tras la recepcion). */
  SWEET_START: 0.1,
  /** Fin del punto dulce. */
  SWEET_END: 0.36,
  /** Distancia maxima al rival para activar el remate. */
  PRESSURE_RADIUS: 78,
  /** Potencia minima del remate (toque seco valido). */
  MIN_POWER: 0.06,
  /** Margen hacia dentro del palo (unidades mundo). */
  POST_INSET: 5,
  /** Bonus de velocidad del remate imparable. */
  SPEED_SCALE: 1.1,
} as const;

/**
 * Cambio de ritmo: fake (toque corto de K) y doble toque (E con balon).
 * Es lo que separa conducir en linea recta de un futbolista que engaña.
 */
export const SKILL = {
  /** Carga maxima que sigue contando como fake y no como pase/tiro flojo. */
  FAKE_MAX_CHARGE: 0.12,
  /** Radio en el que el fake hace dudar al marcador. */
  FAKE_RADIUS: 95,
  /** Segundos de duda del rival tras un fake. */
  FAKE_HESITATE: 0.28,
  /** Impulso propio tras fingir (segundos de burst). */
  FAKE_BURST: 0.22,
  FAKE_COOLDOWN: 0.7,
  /** Impulso de velocidad al explotar el fake / doble toque. */
  BURST_SPEED: 980,
  /** Segundos de aceleracion extra tras un doble toque con E. */
  PACE_BURST: 0.32,
  /** Empuje extra del balon en el doble toque (sobre el toque normal). */
  PACE_TOUCH_GAIN: 1.55,
  /** Durante el burst, el duelo aguanta mas: vas por delante del marcador. */
  BURST_DUEL_SCALE: 1.35,
  /** Mientras dudas, aceleras menos. */
  HESITATE_ACCEL: 0.35,
  /**
   * Corte seco: cambio lateral brusco. Se dispara con fake+strafe o O lateral
   * (cuando no hay caño). El balon recibe un toque al lado y tu sales con burst.
   */
  /** |dot(facing, move)| por debajo de esto cuenta como lateral. */
  CUT_MAX_DOT: 0.42,
  CUT_COOLDOWN: 0.65,
  CUT_BURST: 0.26,
  CUT_BURST_SCALE: 0.82,
  /** Empuje del balon en la direccion del corte (u/s). */
  CUT_BALL_PUSH: 220,
  CUT_HESITATE: 0.3,
  CUT_RADIUS: 100,
} as const;

/**
 * Levantar el balon. Un solo boton con dos lecturas segun lo que estes
 * haciendo, igual que Shift (recibir / mini-pase al soltar):
 *
 *  - **Parado**: "embaixadinha". El balon sube poco y vuelve al pie. No pasa
 *    por encima de nadie (queda por debajo de BALL.LOFT_CLEAR_PLAYERS), pero
 *    supera el umbral de volea, asi que sirve para prepararse un remate.
 *  - **En carrera**: sombrero. Sube por encima de LOFT_CLEAR_PLAYERS, asi que
 *    el balon vuela sobre el rival, y sale empujado hacia delante para que
 *    puedas rebasarlo corriendo y recogerlo al otro lado.
 *
 * La fisica de vuelo ya existia entera (BALL.LOFT_*); esto solo aporta el
 * verbo del jugador.
 */

/**
 * Caño: el balon pasa entre las piernas del rival (loft corto que supera
 * LOFT_CLEAR_PLAYERS un instante) y tu sales al otro lado con burst.
 * Se dispara con O (dash) cuando hay un marcador en el cono frontal.
 */
/**
 * Poner el cuerpo: embiste al rival cercano con la O cuando NO llevas el balon.
 *
 * Existio un dash defensivo y se quito a proposito (ver el comentario en
 * `physics/step.ts`): sin balon hacia lo mismo que la entrada — fijar velocidad
 * hacia una direccion y poner enfriamiento — asi que eran dos botones para el
 * mismo verbo, "ir a por el balon".
 *
 * Esto es un verbo DISTINTO. La entrada (K) va a por el BALON; esto va a por el
 * JUGADOR. No te llevas la posesion nunca: desplazas al rival y lo dejas
 * desequilibrado, y es su mal control el que suelta el balon. Si robara
 * directamente volveriamos a tener dos botones para lo mismo.
 */
export const BODY_CHECK = {
  /** Alcance del embiste, de centro a centro. Poco mas de dos cuerpos. */
  REACH: 58,
  /** Coseno del cono: el rival tiene que estar por delante, no al lado. */
  CONE_COS: 0.4,
  /** Velocidad con la que te lanzas: cierras el hueco y ocupas su sitio. */
  LUNGE_SPEED: 420,
  /**
   * Empuje que se lleva el rival.
   *
   * NO se reparte como en un choque normal (`collideBallWithPlayers` divide el
   * impulso entre los dos). Esa asimetria ES el mecanismo: el que embiste va
   * preparado y el que lo recibe no.
   */
  SHOVE: 520,
  /** Desequilibrio que le metes: mas largo que un choque fortuito (0,35). */
  STUMBLE_TIME: 0.52,
  /** Lo poco que te desequilibras tu. Vas lanzado, pero sabias que ibas. */
  SELF_STUMBLE: 0.12,
  /** Enfriamiento. Mas largo que el regate (0,9): esto es una apuesta. */
  COOLDOWN: 1.15,
  /**
   * Cuanto te metes POR DELANTE del rival al embestirle si lleva el balon.
   *
   * El balon conducido va pegado delante de quien lo lleva, asi que embestir a
   * su centro es empujarle por la espalda: le mueves, pero el balon sigue en su
   * pie y tu detras. Apuntando a este punto —entre su cuerpo y el balon— el
   * cuerpo se mete donde de verdad se roba una posicion en futbol.
   *
   * No hace falta que sea grande: con el radio del jugador (14) mas el del
   * balon, 22 u cae justo en el hueco entre los dos.
   */
  SHIELD_LEAD: 22,
  /**
   * PROTEGERSE: la O del que LLEVA el balon, plantando el cuerpo.
   *
   * Es la respuesta al cuerpo, y por eso el juego se decide en el timing y no
   * en quien pulsa antes: si el que embiste llega mientras dura esta ventana,
   * rebota el —lo mandas lejos y te quedas el balon—; si llega antes o despues,
   * el cuerpo funciona como siempre.
   *
   * 0,22 s es lo que dura un gesto de reaccion humano y no da para cubrirse a
   * ciegas: el enfriamiento del dash (0,9 s) se lo come igual, asi que aporrear
   * la O no te hace invulnerable.
   */
  BRACE_WINDOW: 0.22,
  /**
   * Con lo que sale despedido el que embistio a un rival protegido.
   *
   * Bastante mas que `SHOVE` (520) a proposito: el que se protege ha acertado
   * una ventana de dos decimas, y el premio tiene que verse desde la grada.
   */
  BLOCK_SHOVE: 880,
  /** Y se queda en el suelo mas rato que el que recibe un cuerpo normal. */
  BLOCK_STUMBLE: 0.72,
} as const;

export const NUTMEG = {
  /** Distancia minima al rival para intentar el caño. */
  MIN_DIST: 28,
  /** Distancia maxima al rival. */
  MAX_DIST: 78,
  /** Coseno del semi-angulo del cono (≈35°). */
  CONE_COS: 0.82,
  /**
   * Velocidad vertical: pico ≈ 38u, justo por encima de LOFT_CLEAR_PLAYERS,
   * menos teatral que el sombrero.
   */
  LOFT: 190,
  /** Empuje horizontal base a traves del rival. */
  PUSH_BASE: 280,
  /** Extra de empuje segun distancia al rival. */
  PUSH_PER_DIST: 2.2,
  /** Burst propio al salir del caño. */
  BURST_SCALE: 0.88,
  BURST_TIME: 0.28,
  /** Duda del rival canado. */
  HESITATE: 0.42,
  /** Cooldown propio (ademas del dash). */
  COOLDOWN: 0.85,
  /**
   * Caño / sombrero hacia atras: stick hacia atras. Sombrero sin marcador;
   * caño en carrera, marcador opcional (solo duda al rival cercano).
   */
  /** Stick debe apuntar atras (dot con facing <= -REAR_MOVE_COS). */
  REAR_MOVE_COS: 0.35,
  /** Rival detras del facing (dot <= REAR_CONE_COS). */
  REAR_CONE_COS: -0.2,
  /** Stick alineado con la direccion al rival. */
  REAR_AIM_COS: 0.72,
  /** Velocidad minima en carrera para intentar gesto atras. */
  REAR_MIN_SPEED: 70,
  /** Burst hacia delante tras caño atras (sigues corriendo). */
  REAR_BURST_SCALE: 0.95,
  /** Empuje del balon hacia el marcador (factor sobre push normal). */
  REAR_PUSH_SCALE: 0.88,
  /** Loft del sombrero atras (un poco mas bajo que frontal). */
  REAR_SCOOP_LOFT: 215,
  /** Empuje extra del sombrero atras estando parado. */
  REAR_STAND_PUSH: 55,
} as const;

/**
 * La mira (Espacio). Es lo que separa a un novato de un experto: sin ella el
 * teclado solo da 8 direcciones de tiro; con ella, cualquier angulo.
 *
 * El precio es dejar de moverte mientras apuntas, asi que el reloj del duelo
 * corre en tu contra: apuntar bien es apuntar rapido.
 */
export const AIM = {
  /** Velocidad de giro de la mira (rad/s). Lenta a proposito: colocar cuesta. */
  TURN_RATE: 3.2,
  /**
   * Giro de la mira mientras cargas K.
   *
   * Es el mando de SENSIBILIDAD del golpeo, y ha bajado tres veces por la misma
   * razon: 5,4 -> 3,4 -> 2,1 -> 1,5. Incluso a 2,1 rad/s (~120°/s) la flecha
   * seguia yendose: la comba plena (`KICK.SWEEP_FULL`, 29°) cabia en 0,24 s,
   * que es un toque de tecla, asi que corregir la punteria seguia rosqueando
   * sin querer y "un poco de curva" no se podia pedir.
   *
   * Con 1,5 rad/s (~86°/s) la comba plena pide 0,33 s de direccion mantenida y
   * todo lo de en medio se dosifica con la mano, no con el cronometro.
   *
   * Lo que NO cuesta mas caro es apuntar: la direccion de salida se fija al
   * PULSAR (`lockKickDirection` lee el stick en ese instante), asi que si ya
   * venias apuntando no hay nada que girar. Este numero solo pone precio a
   * cambiar de idea a mitad de carga — que es justo lo que debe costar.
   *
   * TIENE que ser una constante compartida, no un ajuste por jugador: se lee
   * dentro de `stepPlayer`, que en online corre igual en los dos clientes. Un
   * valor distinto en cada maquina desincroniza la partida.
   */
  CHARGE_TURN_RATE: 1.5,
  /** Largo de la linea de mira en unidades de mundo. */
  LINE_LENGTH: 300,
  /**
   * Largo de la flecha de tiro al cargar (solo humano).
   *
   * Era 150 y con eso la comba era INVISIBLE: sobre 96 unidades de flecha, el
   * barrido maximo se doblaba 7 grados y a barridos normales 1 o 2. La curva
   * estaba bien calculada, pero la linea era demasiado corta para enseñarla —
   * y una mecanica que no se ve no se puede aprender. Medido con
   * `pnpm shot:curve`, columna "curva en la mira".
   */
  SHOT_ARROW_LENGTH: 360,
  /** Ventana (s) que representa el anillo de primera abierto del todo. */
  FIRST_TIME_RING_SPAN: 0.3,
  /** Cuanto se abre el anillo de primera sobre el cuerpo (u). */
  FIRST_TIME_RING_REACH: 46,
  /** Calidad a partir de la cual el anillo destella: has clavado el timing. */
  FIRST_TIME_FLASH_AT: 0.82,
  /** Angulo (rad) desde el lock inicial para mostrar la flecha de redireccion. */
  SHOT_REDIRECT_SHOW: 0.14,
  /**
   * Rozamiento mientras se apunta, como fraccion del normal. Apuntar no
   * acelera, pero tampoco clava los pies: se planea.
   *
   * Importa mas de lo que parece. La comba solo se puede buscar apuntando
   * (golpear cruzado a la carrera), y con el rozamiento normal la carrera se
   * evaporaba antes de terminar de girar la mira: la rosca era inalcanzable.
   */
  FRICTION_SCALE: 0.3,
} as const;

/**
 * IA. Los principios son los de un cuerpo tecnico de verdad, reducidos a lo
 * minimo que funciona en 2D:
 *
 *  1. **Forma de equipo**: cada jugador tiene su posicion de formacion y todo
 *     el bloque se desliza con el balon. Es lo que evita el error clasico de
 *     que los once persigan la pelota como alevines.
 *  2. **Presion escalonada**: solo el mas cercano va al balon; el resto
 *     mantiene la forma y cubre.
 *  3. **Coberturas por dentro**: se presiona por el lado del gol, nunca de
 *     espaldas a la porteria propia.
 *  4. **Apoyos con amplitud**: sin balon se busca espacio ancho y por delante,
 *     ofreciendo linea de pase en diagonal.
 *  5. **Decision del que lleva el balon**: disparar si hay rango y angulo,
 *     pasar si hay un companero mejor colocado y el carril esta limpio, y si
 *     no, conducir hacia porteria evitando al rival mas cercano.
 */
export const AI = {
  /** Cada cuanto reevalua su decision. Da tiempo de reaccion humano y evita tiritera. */
  DECISION_INTERVAL: 0.13,
  /** Zona muerta de llegada: dentro de esto ya no corrige, para no vibrar. */
  ARRIVE_RADIUS: 18,
  /** Radio en el que empieza a frenar al llegar. */
  SLOW_RADIUS: 90,
  /** Se coloca a esta distancia del rival con balon, por el lado del gol. */
  PRESS_STANDOFF: 32,
  /**
   * Dentro de este radio deja de contener y va al balon a por todas. Sin esto
   * el defensa se planta a la distancia de contencion, frena, y como robar
   * exige velocidad relativa no roba nunca: el partido se queda congelado.
   */
  TACKLE_RANGE: 88,
  /** Rango desde el que se atreve a disparar. */
  SHOOT_RANGE: 480,
  /**
   * Tan cerca, dispara aunque el carril no este limpio. Sin esto el delantero
   * no chutaba nunca desde el area: el portero siempre tapa el centro de la
   * porteria, asi que se quedaba regateando contra la linea de gol.
   */
  CLOSE_SHOT_RANGE: 260,
  /**
   * Distancia a la que se planta para rematar. Conducir hacia el centro de la
   * porteria empotraba al delantero contra la linea de gol: acampaba en el area
   * sin chutar jamas.
   */
  SHOOT_STANDOFF: 300,
  /**
   * Error de timing de la IA en el golpeo de primera, en dos partes.
   *
   * La IA ve la ventana exacta: sin error clavaria el contacto perfecto SIEMPRE
   * y tendria un gesto que ningun humano puede igualar (medido con solo +/-0.075
   * de sesgo: calidad media 0.88 y casi todo "perfecto").
   *
   *  - `BIAS`: rasgo FIJO por cerebro. Unos jugadores le pegan mejor de primera
   *    que otros, y es estable y reproducible con la semilla.
   *  - `JITTER`: error del intento concreto, sorteado UNA vez al abrirse la
   *    ventana (no por tick: sortearlo cada tick equivale a quedarse con la
   *    tirada mas favorable y el error se evapora).
   *
   * Juntos abarcan hasta ~0.16 s de desvio, que sobre una ventana de 0.3 s
   * llega a bajar la calidad a "flojo".
   */
  FIRST_TIME_BIAS: 0.09,
  FIRST_TIME_JITTER: 0.07,
  /**
   * Puntuacion minima de `evaluateShot` para rematar de primera en vez de
   * controlar. OJO con la escala: NO es 0-1. Medido en 3v3 con los rivales
   * lejos, va de ~16 en campo propio a ~200 pegado al area. Con 60 solo se
   * remata desde el ultimo quinto; con un umbral bajo la IA remata TODO lo que
   * le llega y el partido se convierte en un pinball (medido: 348 tiros en 60 s
   * con el umbral mal puesto).
   *
   * Subido de 60 a 95 tras medir: con 60 salian 9,4 remates de primera por
   * partido en 3v3, y en alguna semilla la IA remataba en vez de disputar hasta
   * bajar los cambios de posesion de 14 a 6. Tiene que ser un recurso, no una
   * costumbre: es el gesto mas vistoso del juego y se gasta si se abusa.
   */
  FIRST_TIME_SHOOT_MIN: 95,
  /** Enfriamiento por jugador tras intentar una primera (s). */
  FIRST_TIME_COOLDOWN: 0.5,
  /** Puntos que tantea a lo ancho de la porteria para buscar el hueco. */
  SHOT_SAMPLES: 7,
  /** Margen respecto al poste al elegir el punto de tiro. */
  SHOT_POST_MARGIN: 26,
  /** Escala de carril libre al puntuar un disparo. */
  SHOT_CLEAR_SCALE: 95,
  /**
   * Caida del score de tiro con la distancia, y suelo de esa caida.
   *
   * La version anterior interpolaba de 1 (en `CLOSE_SHOT_RANGE`) a 0,2 (en
   * `SHOOT_RANGE`), y eso hacia el tiro lejano **matematicamente imposible**:
   * con el carril perfecto (`clearNorm` 1,2) el score maximo a rango largo era
   * 1,2 x 0,2 x 100 = 24, por debajo de `MIN_SHOT_SCORE` (38). No es que la IA
   * decidiera no tirar de lejos: no podia. Medido: el 100% de los tiros salian
   * de zona de gol, en los cuatro modos.
   *
   * Con caida 0,7 y suelo 0,45, a rango maximo el techo pasa a 54 — por encima
   * del umbral, pero **solo si el carril esta despejado**: con un carril medio
   * (0,6) el score se queda en 27 y no dispara. Que es justo el criterio real:
   * de lejos se chuta cuando lo ves, no porque si.
   */
  SHOT_DIST_FALLOFF: 0.7,
  SHOT_DIST_FLOOR: 0.45,
  /** Error angular del disparo a rango maximo (radianes). */
  SHOT_SPREAD: 0.11,
  /**
   * Portero adelantado: si su profundidad respecto a la linea supera esto,
   * la IA puede intentar vaselina en el mano a mano. Debe estar claramente
   * por encima de GK_LINE_DEPTH (~78): solo cuando achica de verdad.
   */
  CHIP_GK_RUSH_DEPTH: 125,
  /** Distancia maxima a la linea de gol para plantear chip. */
  CHIP_MAX_RANGE: 230,
  /** Bonus de score al detectar portero achicando. */
  CHIP_SCORE_BONUS: 42,
  /** Potencia tipica de vaselina (carga 0..1). */
  CHIP_POWER: 0.38,
  /** Potencia tipica de tiro colocado al palo. */
  PLACED_POWER: 0.34,
  /** Puntuacion minima para disparar de lejos. */
  MIN_SHOT_SCORE: 38,
  /** Puntuacion minima para disparar dentro del area. */
  MIN_CLOSE_SHOT_SCORE: 28,
  /** Puntuacion minima para pasar. */
  /**
   * Umbral de pase en escala CRUDA. Solo lo usa `evaluatePass` por dentro
   * (pared y pase al hueco), donde todo se compara en crudo. Para comparar
   * contra el tiro esta `MIN_PASS_SCORE_NORM`.
   */
  MIN_PASS_SCORE: 42,
  /**
   * Ganancia y pivote de `normalizeActionScore`, que lleva pase y centro a la
   * escala del tiro. Calibrados sobre la distribucion medida (8 partidos por
   * modo) para que el pase mediano caiga en ~15 y el p95 en ~70, que es donde
   * esta el tiro. Ver el comentario largo en `attack.ts`.
   */
  ACTION_SCORE_GAIN: 42,
  ACTION_SCORE_PIVOT: 1500,
  /**
   * Umbral de pase YA NORMALIZADO, el que usa `chooseAttack`.
   *
   * Es `normalizeActionScore(MIN_PASS_SCORE)`: el mismo percentil de siempre,
   * asi que que pases se consideran validos NO cambia. Lo que cambia es que
   * ahora el pase y el tiro compiten en la misma escala.
   */
  MIN_PASS_SCORE_NORM: 1.2,
  /** El pase tiene que superar al disparo por este factor para elegirse. */
  PASS_SHOOT_BIAS: 1.08,
  /** Bajo presion, se favorece mas el pase. */
  PASS_PRESSURE_BIAS: 0.92,
  /** Segundos de anticipacion al pasar a un companero en carrera. */
  PASS_LEAD_TIME: 0.28,
  /** Bonus si el receptor esta muy desmarcado. */
  /**
   * Cuanto se prefiere pasar a un companero humano frente a un bot igual de bien
   * situado. Es la palanca que decide si el jugador se siente parte del equipo.
   *
   * No puede ser enorme: si la IA regala el balon al humano en situaciones
   * malas, deja de parecer un equipo y pasa a parecer un tutorial. 1.35 = a
   * igualdad de condiciones, se la damos a la persona.
   */
  HUMAN_PASS_BONUS: 1.35,
  PASS_OPEN_BONUS_DIST: 130,
  /** Por debajo de esto, el companero esta marcado de cerca. */
  MARKED_RADIUS: 88,
  /** Por encima de esto, el companero esta claramente libre. */
  FREE_RADIUS: 125,
  /** Multiplicador de score al pasar a un companero libre. */
  PASS_FREE_BONUS: 1.85,
  /** Penalizacion al pasar a alguien marcado. */
  PASS_MARKED_PENALTY: 0.42,
  /** Bonus por pase a pared cuando el carril directo esta tapado. */
  PASS_WALL_BONUS: 1.55,
  /**
   * MENOS PASES A LA PARED (2026-09-29). Con el pase directo a ese compañero
   * LIMPIO, la pared tiene que puntuar esto veces mas que el directo (era
   * 0,95: en la practica, ahora nunca). Y aunque el directo a ESE compañero
   * este tapado, la pared pierde contra el mejor pase directo a otro si este,
   * multiplicado por `WALL_OVER_ANY_DIRECT`, la iguala (0 = como antes).
   *
   * Medido (`pnpm posicion:probe`): del 3v3 al 7v7 el 20-33 % de los pases
   * iban a la banda, y en el 55-68 % de esos habia un compañero libre por el
   * centro con el pase directo limpio. Ahora, el 4-9 %. Y con `pnpm ai:audit`
   * (24 partidos): acierto de pase 70-80 % → 75-83 %, interceptados -2 a -7
   * puntos, mas pases por posesion, goles iguales.
   */
  WALL_OVER_DIRECT: 100,
  WALL_OVER_ANY_DIRECT: 2,
  /**
   * Tiki-taka / juego de posicion. Pase corto, un toque, triangulos.
   * Distancia maxima de un pase de combinacion.
   */
  SHORT_PASS_MAX: 240,
  /**
   * Pases directos (`TeamInstructions.directness`). Con la instruccion en 1,
   * el pase largo hacia delante vale `1 + GAIN` y el corto `1 - GAIN`; en 0,
   * al reves; en 0,5 (fabrica) todo multiplica por 1 exacto. "Largo" se
   * escala de `SHORT_PASS_MAX` a `DIRECT_LONG_PASS`.
   */
  DIRECTNESS_GAIN: 0.6,
  DIRECT_LONG_PASS: 440,
  /** Segundos minimos de control para poder soltar de un toque. */
  ONE_TOUCH_HOLD: 0.06,
  /** Bajo esta presion, el un-toque es casi obligatorio si hay apoyo. */
  ONE_TOUCH_PRESSURE: 92,
  /** Bonus de score por pase corto limpio. */
  TIKI_SHORT_BONUS: 1.48,
  /**
   * El bonus de combinacion corta, en campo propio. Sin esto la triangulacion
   * puntuaba igual tocandola entre centrales que al borde del area, y el equipo
   * se pasaba el partido dando vueltas atras: 96 pases por 3 tiros y el balon
   * en el ultimo tercio solo el 26% del tiempo. Combinar cerca del area es un
   * plan; combinar en tu campo es no salir de ahi.
   */
  TIKI_OWN_HALF_SCALE: 0.5,
  /** Bonus si el pase forma triangulo con un tercero. */
  TRIANGLE_PASS_BONUS: 1.55,
  /** Bonus al tercer hombre (no devolver al que te la dio). */
  THIRD_MAN_BONUS: 1.28,
  /** Penaliza el muro A→B→A inmediato (salvo bajo presion). */
  RETURN_PASS_PENALTY: 0.52,
  /**
   * Cuanto suma cada salida limpia que tendria el receptor de un pase atras.
   * Con 0,45, un central libre con dos salidas casi duplica el valor del pase.
   */
  /**
   * Cuanto avance "vale" la construccion. Un receptor libre con dos salidas
   * limpias equivale a progresar unas 160 unidades: compite de tu a tu con un
   * pase hacia delante de verdad, sin ganarle siempre.
   */
  /**
   * Cuanto mas exigente es un pase de construccion que uno normal. Retrasar el
   * balon tiene que ganarselo, no ser la salida comoda cuando no se ve nada.
   */
  BUILDUP_MIN_SCORE_SCALE: 0.85,
  BUILDUP_ADVANCE_EQUIV: 130,
  BUILDUP_OUTLET_GAIN: 0.45,
  /** Un companero cuenta como salida si esta al menos esto por delante. */
  BUILDUP_OUTLET_AHEAD: 70,
  /**
   * Multiplicador si el receptor esta presionado. Devolversela a alguien con
   * un rival encima no es construir, es pasarle el problema.
   */
  BUILDUP_CROWDED_SCALE: 0.55,
  /** Preferir circular antes que conducir si hay apoyo corto. */
  CIRCULATE_DRIBBLE_BIAS: 0.72,
  /** Bonus base de un centro bien timed desde banda. */
  CROSS_BONUS: 1.65,
  /** Ventana de timing (s): receptor llegando al punto de caida. */
  CROSS_TIMING_WINDOW: 0.38,
  /** Puntuacion minima para centrar. */
  /** Umbral de centro en escala CRUDA (uso interno de `evaluateCross`). */
  MIN_CROSS_SCORE: 48,
  /**
   * Umbral de centro ya normalizado, para `chooseAttack`. El centro se
   * normaliza con la MISMA funcion que el pase: al ser monotona, la eleccion
   * entre uno y otro se conserva, y ambos pasan a competir de tu a tu con el
   * tiro.
   */
  MIN_CROSS_SCORE_NORM: 1.3,
  /** Bajo esta distancia al marcador, la IA puede fingir. */
  FAKE_PRESSURE: 70,
  /** Probabilidad (0-1) de fingir al decidir bajo presion. */
  FAKE_CHANCE: 0.38,
  /** Probabilidad de intentar caño cuando el rival esta en cono. */
  NUTMEG_CHANCE: 0.14,
  /** Probabilidad de corte seco (fake lateral) al escapar. */
  CUT_CHANCE: 0.22,
  /*
   * EL CUERPO (2026-09-28). La O que ya tenia el humano (`BODY_CHECK`): sin
   * balon, embestir al que lo lleva; con balon, plantarse para que rebote el
   * que te embiste. Medido antes: en 240 partidos la IA embistio 0 veces y se
   * planto 0 (los escudos que armaba eran de rebote, al acelerar con el
   * balon). Los dos gestos escalan con el nivel (`bodyScale` en
   * `ai/skill.ts`): el barrio no los usa, como alguien que acaba de coger el
   * mando.
   *
   * Medido con `pnpm body:probe` (IA contra IA, con cuerpo y aguantar de
   * espaldas): la posesion de equipo dura un 9-18 % mas, se pierde el balon un
   * 7-19 % menos y hay un 15-20 % menos de goles, segun el modo.
   */
  /**
   * Probabilidad, en cada decision, de embestir al que lleva el balon si esta
   * a tiro (`BODY_CHECK.REACH`). Solo de frente o de lado: por la espalda seria
   * un empujon, y es justo lo que deja al que se cubre de espaldas aguantar.
   */
  BODY_CHECK_CHANCE: 0.3,
  /** Coseno minimo entre hacia donde mira el que lleva el balon y el que embiste. */
  BODY_CHECK_FRONT_COS: -0.3,
  /**
   * Probabilidad de plantarse cuando un rival va a tenerle a tiro dentro de
   * `BRACE_LOOKAHEAD` segundos y lo que tocaba era conducir. Si tocaba pasar o
   * tirar, se suelta el balon: es la mejor defensa contra un cuerpo.
   */
  BRACE_CHANCE: 0.6,
  /**
   * Lo que mira hacia delante: toda la ventana del escudo. Con menos, como la
   * IA decide cada 0,11-0,18 s, el rival entraba a tiro ENTRE dos decisiones y
   * no se le veia venir — contra un humano que embiste en cuanto puede, 0 de
   * 14 rebotes.
   */
  BRACE_LOOKAHEAD: 0.22,
  /**
   * AGUANTAR DE ESPALDAS: si le aprietan de frente (el rival entre el y la
   * porteria, a menos de `SHIELD_PRESSURE`) y tiene un compañero de campo a
   * menos de `SHIELD_SUPPORT`, el que lleva el balon se da la vuelta y lo
   * cubre, alejandose del rival: el balon queda al otro lado de su cuerpo, que
   * es lo que exige `POSSESSION.SHIELD_TAKE_MARGIN` para robarlo, y la
   * embestida de la IA no entra por la espalda. Cuando se abre el pase, se
   * juega (las ramas de pase se miran antes que esta en cada decision).
   *
   * Hasta `SHIELD_MAX_HOLD` segundos con el balon: despues se busca la salida
   * de siempre, que aguantar sin fin es esperar a que llegue el segundo rival.
   */
  SHIELD_PRESSURE: 90,
  SHIELD_AHEAD_COS: 0.2,
  SHIELD_SUPPORT: 320,
  SHIELD_STEP: 80,
  SHIELD_MAX_HOLD: 1.6,
  /**
   * Solo hasta aqui (fraccion del campo recorrida hacia la porteria rival).
   * Mas arriba, con alguien delante, se encara o se remata: aguantando ahi la
   * IA tiraba un 17 % menos en 7v7.
   */
  SHIELD_MAX_ADVANCE: 0.62,
  /** Bonus por pase ancho. */
  PASS_WIDE_BONUS: 1.18,
  /** Bonus por cambio de juego al lado opuesto del balon. */
  PASS_SWITCH_BONUS: 1.28,
  /** Bonus por pase de seguridad a defensa bajo presion. */
  PASS_SAFETY_BONUS: 1.45,
  /*
   * PASES QUE LLEGAN (2026-09-28). Medido con `pnpm ai:audit`: acierto de pase
   * 61-73 % (el de la Premier League ronda el 81 %), 0,8-2,0 pases por
   * posesion (3,5-4,7 en la Premier, mediana de 2 en futbol sala), posesiones
   * de 2,5-3,2 s (9,5 s en la Premier, 8,6 en sala), y la IA conduciendo en el
   * 43-73 % de sus decisiones: mas de la mitad de las perdidas eran con el
   * balon en el pie.
   *
   * Calibrado como el pase esperado (xP) de Opta —riesgo al golpear contra si
   * llego—, lo que predice si un pase llega es la holgura del carril
   * (`laneClearance`): <15 u llega el 41-47 %, 30-50 u el 77-86 %, >80 u el
   * 91-92 %. Un modelo por tiempos (quien llega antes a cada punto de la
   * linea) se probo y NO separaba mejor: marcaba "muy arriesgados" la mitad de
   * los pases y esos llegaban el 78-80 %.
   */
  /**
   * RECICLAR: apretado (rival a menos de `RECYCLE_PRESSURE`) y a punto de
   * conducir contra el, si hay un compañero con el carril limpio (holgura de
   * `RECYCLE_MIN_LANE` o mas: de 30 a 50 u llegan el 77-86 %) y sin nadie a
   * menos de `RECYCLE_MIN_OPEN`, se la da, aunque sea atras o al lado. Es lo
   * que Football Manager llama reciclar la posesion ("la opcion aburrida cuando
   * la agresiva seria un suicidio") y el "Possession Game" de eFootball.
   * Escala con el nivel (`readScale`): el barrio no recicla. 0 en
   * `RECYCLE_MIN_LANE` = como antes.
   *
   * Medido con `pnpm ai:audit` (16 partidos por modo), con el pase atras largo
   * de `backPassReach` y la salida al portero: acierto de pase 61-70 % → 71-83 %,
   * pases por posesion 0,8-1,8 → 1,5-3,3 (11v11: 3,3, la Premier 3,5-4,7) y
   * posesiones acabadas en tiro iguales o mas (11v11 16 → 22 %). Los umbrales
   * se probaron mas estrictos (lane 50, abierto 70, presion 95): reciclaba el
   * 2-6 % de las decisiones y apenas se notaba.
   */
  RECYCLE_PRESSURE: 130,
  RECYCLE_MIN_LANE: 35,
  RECYCLE_MIN_OPEN: 50,
  /**
   * Entre los pases seguros, reciclar prefiere el que menos terreno pierde:
   * pesa `1 + avance / (2 * RECYCLE_GROUND)`, de 0,5 (esto de atras) a 1,5 (esto
   * de delante). Sin esto el central libre y lejos ganaba siempre, y el 30-42 %
   * de los pases acababan yendo hacia atras.
   */
  RECYCLE_GROUND: 240,
  /**
   * Y si no hay compañero de campo seguro, AL PORTERO: en campo propio (hasta
   * `RECYCLE_GK_MAX_ADVANCE` del campo), con el carril el doble de limpio y
   * ningun rival a menos de `RECYCLE_GK_ROOM` del portero. Es la salida atras
   * mas usada del futbol de verdad y del futbol sala ("salir jugando desde
   * atras"), y la IA no la usaba nunca: `evaluatePass` descarta al portero. En
   * 3v3, con un solo compañero de campo, era la unica opcion que faltaba.
   * 0 en `RECYCLE_GK_ROOM` = sin portero.
   */
  RECYCLE_GK_MAX_ADVANCE: 0.5,
  RECYCLE_GK_ROOM: 170,
  /**
   * DESBORDE POR BANDA (rama `desborde:banda`, solo con bandas: de 6v6 arriba).
   * El extremo o el lateral con balon, pegado a la linea (a mas de `DRIVE_WIDE`
   * del centro) y en campo rival (a menos de `DRIVE_MAX_DEPTH` de la linea de
   * gol), si la banda esta libre por delante (`DRIVE_LANE` con los rivales de
   * delante), en vez de reciclar conduce hacia la linea de fondo, `DRIVE_STEP`
   * cada vez, hasta `DRIVE_MIN_DEPTH`. El que ya va lanzado la termina.
   *
   * Existe porque se centraba poco: el portador pisaba la zona de centro el
   * 5-7 % del tiempo con balon, y con un centro disponible reciclaba el 22-27 %
   * de las veces. Crece con el nivel (`decisionSlack`). 0 en `DRIVE_CHANCE` =
   * como antes.
   */
  DRIVE_CHANCE: 0.6,
  DRIVE_WIDE: 290,
  DRIVE_MAX_DEPTH: 900,
  DRIVE_MIN_DEPTH: 170,
  DRIVE_STEP: 150,
  DRIVE_LANE: 50,
  /** El que ya va lanzado (a mas de esta velocidad y asi de alineado) la termina. */
  DRIVE_MIN_SPEED: 60,
  DRIVE_HEADING: 0.8,
  /**
   * CENTRO DESDE EL FONDO (rama `centro:desde-el-fondo`): en la zona de centro
   * y a menos de esto de la linea de gol, con alguien dentro del area, se
   * centra con el liston normal (`MIN_CROSS_SCORE_NORM`), no con el triple que
   * exige el centro temprano. Como en el futbol de verdad, la mayoria no
   * llegan: es la opcion natural al llegar al fondo, no la segura. 0 = como antes.
   *
   * Medido con `pnpm cross:probe` (40 partidos de 3 min), junto con
   * `CROSS_ARRIVAL_LEAD`, `CROSS_WIDE_CHANCE` y `TACTICS.BOX_SKIP_OWNER`:
   * centros por equipo en 5 min 3,8 → 5,5 en 5v5, 4,1 → 5,1 en 7v7 y
   * 4,0 → 4,4 en 11v11; goles +3 %, -1 % y +10 %. El desborde (`DRIVE_*`) no
   * suma centros: hace que los ponga el extremo desde la linea.
   */
  CROSS_BYLINE_DEPTH: 420,
  /**
   * El centro desde el fondo cuenta tambien con quien esta ENTRANDO: donde
   * estara en estos segundos. Sin esto el area estaba vacia al llegar al fondo
   * (5v5 el 82 % de las veces, 7v7 el 68 %): los rematadores iban de camino.
   */
  CROSS_ARRIVAL_LEAD: 0.5,
  /**
   * CENTRO EN VEZ DE RECICLAR (rama `centro:antes-que-reciclar`): en la zona de
   * centro, con alguien dentro del area o entrando y un centro que pase el
   * liston normal, se centra en vez de devolverla atras o seguir conduciendo,
   * con esta probabilidad por decision. Medido antes: con un centro asi
   * disponible, la IA reciclaba el 23-31 % de las veces y conducia otro 25 %.
   * 0 = como antes.
   */
  CROSS_WIDE_CHANCE: 0.5,
  /**
   * Dispersion base de pases IA. Bajada -25% (0.045 -> 0.034) como ayuda de
   * pase deliberada: menos pases absurdos que van directos al rival mas
   * cercano. Sigue habiendo dispersion real — no es tiro laser — para no
   * "chetar" a la IA por encima de lo que decide bien por si sola.
   */
  PASS_SPREAD: 0.034,
  /** Un pase solo compensa si el companero esta esto mas cerca del gol. */
  PASS_MIN_ADVANTAGE: 75,
  /** Un rival a menos de esto del carril lo considera bloqueado. */
  LANE_CLEARANCE: 28,
  /**
   * Margen sobre el RADIO DEL BALON por debajo del cual un carril no se
   * considera abierto, pase lo que pase con el resto de multiplicadores.
   *
   * Es el suelo fisico: por un hueco mas estrecho que el balon mas esto, el
   * pase no pasa. Sin el, las combinaciones cortas bajo presion pedian menos
   * holgura que el propio balon.
   */
  LANE_MIN_MARGIN: 6,
  /** Error angular base de pases IA (rad), antes de distancia y presion. */
  PASS_ERROR_BASE: 0.028,
  /** A esta distancia (u) el error angular ~duplica PASS_ERROR_BASE. */
  PASS_ERROR_DIST_SCALE: 520,
  /** Multiplicador de error bajo presion cercana. */
  PASS_ERROR_PRESSURE: 1.45,
  /** Distancia minima de ventaja para intentar pase al hueco. */
  THROUGH_MIN_SPACE: 55,
  /** Profundidad base del pase al hueco (u). */
  THROUGH_DEPTH: 95,
  /**
   * Cuanto se pone el balon POR DETRAS de la linea del penultimo defensa.
   *
   * Es lo que convierte un pase largo en un pase que rompe: si cae por delante
   * de la linea, el receptor lo recibe de espaldas y con el central encima, que
   * es exactamente la jugada que no se queria.
   */
  THROUGH_BEHIND_LINE: 70,
  /** Margen con la linea de gol: mas alla, el pase se lo queda el portero. */
  THROUGH_GOAL_MARGIN: 190,
  /** A partir de aqui la cobertura obliga a desviar el pase de lado. */
  THROUGH_COVER_RADIUS: 150,
  /** Cuanto se aparta el balon del defensa que cubre (u). */
  THROUGH_COVER_SHIFT: 78,
  /** Si el balon no adelanta ni esto, es un pase al pie con otro nombre. */
  THROUGH_MIN_GAIN: 45,
  /**
   * Tramo que se ahorra el receptor por ir ya lanzado, en segundos de su
   * velocidad. No es velocidad extra (eso contaria dos veces): es no tener que
   * frenar, girar y arrancar.
   */
  THROUGH_MOMENTUM: 0.35,
  /** Lo que tarda un defensa en leer el pase y salir. El portero no la paga. */
  THROUGH_RIVAL_REACTION: 0.12,
  /**
   * Lo que cuesta frenar y darse la vuelta, en segundos, al que va lanzado en
   * sentido contrario al balon.
   *
   * Es la premisa entera del pase al hueco: va a la espalda del defensa, que
   * viene subiendo, mientras el que remata ya va de cara. Sin esto el defensa
   * cambiaba de sentido gratis y no se ganaba una sola carrera.
   */
  THROUGH_TURN_TIME: 0.42,
  /** Ventaja minima en la carrera para intentarlo (s). Por debajo, se regala. */
  THROUGH_MIN_RACE: 0.08,
  /** Ventaja (s) a partir de la cual se llega comodo, no por los pelos. */
  THROUGH_RACE_COMFORT: 0.9,
  /**
   * Profundidades que se prueban, de mas a menos, sobre el carril del hueco.
   * Se coge la primera que el companero GANA: se pone tan lejos como pueda
   * llegar, no tan lejos como se pueda pegar.
   */
  THROUGH_DEPTH_STEPS: [1, 0.66, 0.36] as const,
  /**
   * Fraccion del alcance del balon que se admite en un pase al hueco.
   *
   * Cerca del alcance maximo el balon se arrastra —la ultima quinta parte se
   * recorre en la mitad del tiempo total— y ahi no hay carrera que ganar.
   */
  THROUGH_MAX_REACH: 0.72,
  /** Al hueco se va con el balon tenso: nunca por debajo de esto. */
  THROUGH_MIN_POWER: 0.62,
  /** Penalizacion xG en centros desde angulo muy cerrado (< ~20 deg). */
  CROSS_TIGHT_ANGLE_XG: 0.52,
  /** Penalizacion xG en centros rasos al primer poste. */
  CROSS_NEAR_POST_XG: 0.62,
  /** Angulo minimo (rad) desde la linea de fondo para centros "naturales". */
  CROSS_MIN_ANGLE: 0.42,
  /** Amplitud de referencia del apoyo, para puntuar pases a banda. */
  SUPPORT_WIDTH: 155,
  /** Con un rival mas cerca que esto, se protege con control cerrado. */
  PRESSURE_RADIUS: 78,
  /**
   * Margen sobre la carrera por el balon a partir del cual la IA cierra el
   * control. Si un rival esta a menos de `mi_distancia_al_balon + esto`, va
   * camino de ganarle la interceptacion (`findDisruption`, rama `take`) y hay
   * que proteger el toque.
   *
   * Es distinto de `PRESSURE_RADIUS`, que mide al JUGADOR: conduciendo, el
   * balon va por delante y quien lo roba no necesita acercarse al cuerpo.
   */
  BALL_THREAT_MARGIN: 90,
  /** Portero: distancia a la que sale de la linea de gol. */
  GK_LINE_DEPTH: 78,
  /** Portero: si el balon entra en este radio, sale a por el. */
  GK_RUSH_DISTANCE: 260,
  /** Portero: solo lee el disparo si viene de esta distancia o menos. */
  GK_REACT_DISTANCE: 780,
  /**
   * Portero: error al leer por donde va a cruzar el balon. Con prediccion
   * exacta lo paraba absolutamente todo y no se marcaba un gol. Con paradas
   * fisicas activas, un error menor sigue dejando huecos en palos altos/bajos.
   */
  GK_PREDICTION_ERROR: 28,
  /**
   * Ruido de colocacion sembrado por jugador. DESACTIVADO (2026-08-02).
   * Ver `BOLDNESS_VARIATION` para el motivo y la medicion.
   */
  POSITION_NOISE: 0,
  /**
   * Variacion individual de audacia sobre la base del rol, fija por jugador y
   * sembrada. DESACTIVADA (2026-08-02), antes 0.18.
   *
   * Dos motivos, uno de diseno y otro de aprendizaje:
   *
   *  - Diseno: Golazo va hacia "todos iguales" estilo Haxball. Lo que debe
   *    distinguir a un central de un delantero es la ZONA que ocupa y lo que
   *    hace, no un numero oculto. El perfil por rol (`ROLE_PROFILES.boldness`,
   *    gk 0.70 → st 1.25) SE MANTIENE: eso no es un atributo, es el esqueleto
   *    tactico, y ademas el rol viaja en la observacion RL.
   *
   *  - Aprendizaje: al ser sembrada e invisible, dos estados identicos para un
   *    agente producian acciones distintas. La politica de la IA dejaba de ser
   *    funcion de la observacion y ni la clonacion ni DAgger podian converger.
   *
   * Medido antes de quitarla (A/B, 11 semillas x 3 modos, `pnpm sim`): no se
   * hunde nada. Goles 3v3 5.2→5.0, 5v5 4.5→4.8, 7v7 2.3→2.7 (dentro del ruido)
   * y el ultimo tercio SUBE en los tres modos: 52→57%, 39→40%, 28→32%. O sea
   * que la mejora que en su dia se atribuyo a la audacia venia del perfil de
   * ROL, no de la variacion individual.
   */
  BOLDNESS_VARIATION: 0,
  /**
   * Cuanto estrecha la audacia el carril que se exige para intentar un pase
   * directo. Un jugador audaz intenta el pase filtrado por un hueco que uno
   * conservador considera tapado. Bajo a proposito: la diferencia tiene que
   * notarse en las decisiones, no convertir al delantero en un generador de
   * perdidas.
   */
  BOLDNESS_LANE_GAIN: 0.25,
  /**
   * Cuanto baja la audacia el umbral de disparo. Mismo criterio: un delantero
   * audaz remata desde donde un central ni lo piensa.
   */
  BOLDNESS_SHOT_GAIN: 0.2,
  /**
   * Ayuda de recepcion: cuanto se corrige el movimiento hacia el balon
   * entrante cuando la IA se esta preparando para recibirlo (ver
   * `isBracingForBall`). Un valor bajo, mezclado con el objetivo tactico de
   * siempre (no lo sustituye) — sin esto, un balon que llega fuera de la
   * ruta de posicionamiento actual se recibe mal aunque el jugador ya
   * estuviera sujetando Shift. Deliberadamente parcial: es ayuda a
   * colocarse, no un teletransporte al balon.
   */
  RECEPTION_ASSIST_BLEND: 0.3,
  /**
   * Portero con balon: segundos minimos antes de sacar. Sin esto salia en el
   * mismo tick en que paraba o recibia un pase atras, un saque instantaneo sin
   * criterio (y sin tiempo de que el resto del equipo se abra a recibir).
   * Mas corto que ONE_TOUCH_HOLD de campo porque bajo presion en el area
   * propia cada decima cuenta el doble.
   */
  GK_MIN_HOLD: 0.16,
  /**
   * Segundos que el que saca de centro asienta el balon antes de jugarlo.
   *
   * Sin esto la IA sacaba en el primer tick de decision: el balon se iba dos
   * fotogramas despues de que el arbitro pitara, que se lee como un robot, no
   * como un futbolista. Es el mismo motivo (y el mismo orden de magnitud) que
   * `GK_MIN_HOLD`: mirar arriba antes de tocar.
   */
  KICKOFF_MIN_HOLD: 0.35,
  /**
   * Portero: segundos que puede AGUANTAR el balon si no hay a quien sacar.
   *
   * Existe porque el portero ahora puede decir que no: si ningun companero
   * tiene el pasillo limpio, esperar suele abrir una linea en menos de un
   * segundo (los companeros se estan moviendo). Pero no puede esperar sin fin —
   * ahi le roban— asi que al pasar de aqui despeja a espacio vacio.
   */
  GK_MAX_HOLD: 2.2,
  /**
   * Portero: potencia del saque corto/rodado. Baja a proposito — es un pase
   * de salida a un defensa libre, no un despeje; que llegue con el balon
   * controlable, no que se lo lleve por delante.
   */
  GK_SHORT_KICK_POWER: 0.34,
  /**
   * Portero: potencia del despeje largo. Cerca del maximo: hay que sacar el
   * balon lejos de la propia area cuando no hay salida corta limpia, no
   * quedarse a mitad de camino donde lo recupera el primer rival que llega.
   */
  GK_LONG_KICK_POWER: 0.92,
  /**
   * Portero: dispersion angular del despeje largo (rad). Un punterazo bajo
   * presion no es un pase milimetrado; algo de error es mas realista que
   * clavarlo siempre en el mismo punto, y evita que el rival aprenda a
   * anticiparlo con solo mirar a que companero mira el portero.
   */
  GK_LONG_KICK_SPREAD: 0.09,
  /**
   * Portero: cuanto se adelanta el punto de caida del despeje largo respecto
   * al companero elegido (unidades), en la direccion de ataque. Un despeje al
   * pie exacto del delantero es un regalo al defensa que lo marca; cae un
   * poco por delante para que el companero lo dispute en carrera.
   */
  GK_LONG_CLEAR_LEAD: 90,
} as const;

/**
 * Portero: fisica de parada y tiempos de reaccion. Separado de la IA para que
 * la mecanica de parada viva en simulacion aunque cambie el cerebro.
 */
export const GK = {
  /** Holgura sobre cuerpo+balon para registrar parada. */
  SAVE_PAD: 4,
  /** Alcance extra de estirada hacia palos (unidades). */
  /**
   * Alcance extra de estirada. Subido de 11 a 30 junto con la reaccion: el
   * portero llegaba tarde a todo lo que no le llegase al cuerpo. Barrido
   * medido en 5v5/7v7 — con 40 el 5v5 baja al 6% de conversion, que ya son
   * demasiado pocos goles.
   */
  DIVE_REACH: 30,
  /** Fraccion de velocidad hacia porteria que se absorbe en parada. */
  ABSORB: 0.8,
  /** Restitucion al desviar el rebote de parada. */
  DEFLECT_RESTITUTION: 0.38,
  /** Por debajo de esta velocidad, parada "atrapada" (balon a los pies). */
  CATCH_SPEED: 520,
  CATCH_ABSORB: 0.94,
  CATCH_RESTITUTION: 0.12,
  /**
   * Por encima de esta velocidad, aunque llegue, la inercia suelta el balon
   * (fumble): rebote vivo en el area.
   */
  FUMBLE_SPEED: 1380,
  FUMBLE_ABSORB: 0.32,
  FUMBLE_RESTITUTION: 0.62,
  /**
   * Altura por encima de la cual el balon pasa por encima del portero
   * (vaselina). Por debajo, puede estirarse.
   */
  CHIP_CLEAR_HEIGHT: 52,
  /** Velocidad minima del balon para activar parada. */
  THREAT_MIN_SPEED: 300,
  /**
   * SALTO. La estirada gana alcance a lo ANCHO; esto lo gana a lo ALTO.
   *
   * Antes, cualquier balon por encima de `CHIP_CLEAR_HEIGHT` era gol y punto:
   * la vaselina no era un recurso, era un boton de marcar. Ahora el portero
   * puede intentarlo — y puede fallar.
   *
   * Lo que lo mantiene honesto, y hay que tocarlo con cuidado:
   *
   *  - **Saltando NO gana alcance lateral.** Ni brazo ni anillo: solo el
   *    cuerpo. Una vaselina a la escuadra le sigue pasando por encima, porque
   *    el problema ahi no es la altura.
   *  - **Solo salta a balones que de verdad le pasarian.** Por debajo de
   *    `JUMP_MIN_LOFT` llega de pie y saltar seria ridiculo.
   *  - **Lee la altura A LA LLEGADA**, no la de ahora: un balon que sube mucho
   *    y cae antes de la linea no merece salto.
   *  - **Se equivoca** (`JUMP_READ_ERROR`) y **se queda en el suelo despues**
   *    (`JUMP_COOLDOWN`, mas largo que el de la estirada). El rechace tras un
   *    salto es de quien lo cace.
   */
  /** Segundos en el aire. */
  JUMP_TIME: 0.32,
  /** Segundos hasta poder volver a saltar. Mas caro que la estirada. */
  JUMP_COOLDOWN: 0.85,
  /** Altura extra que alcanza saltando, sobre `CHIP_CLEAR_HEIGHT`. */
  JUMP_REACH: 34,
  /** Impulso horizontal del salto. Pequeño: salta hacia arriba, no de lado. */
  JUMP_SPEED: 110,
  /** Error al leer por donde va a cruzar el balon, en unidades. */
  JUMP_READ_ERROR: 30,
  /** Mas lejos de esto no se plantea saltar. */
  JUMP_MAX_BALL_DIST: 380,

  /** Segundos base antes de moverse a un disparo leido. */
  REACTION_BASE: 0.065,
  /** Segundos extra de reaccion por cada 100 u/s del balon. */
  REACTION_PER_SPEED: 0.028,

  /**
   * ESTIRADA. Hasta 2026-08-26 el portero no se lanzaba: `DIVE_REACH` le daba
   * un anillo de alcance extra y punto, asi que un disparo al angulo era o
   * inalcanzable o se paraba solo, sin que se viera nada.
   *
   * Ahora se compromete: pega un salto en una direccion y **estira el brazo**
   * en esa misma direccion. El alcance deja de ser un circulo y pasa a ser una
   * capsula desde el cuerpo hasta la mano, lo que hace que la estirada tenga
   * DIRECCION — y por tanto que elegirla mal se pague.
   */
  /** Segundos que dura el vuelo. */
  DIVE_TIME: 0.2,
  /**
   * Impulso del salto. Se pasa por `modeSpeed()`, como el dash y la entrada:
   * en 11v11 el juego va a mitad de gas y un portero volando a velocidad de
   * 3v3 pararia cosas que en ese ritmo nadie deberia parar.
   */
  DIVE_SPEED: 280,
  /**
   * Largo del brazo, en MULTIPLOS DEL RADIO DEL PORTERO — no en unidades.
   *
   * Primer intento: 42 unidades fijas. Es el mismo error que ya se cometio con
   * el larguero. El radio de jugador va de 25 (3v3) a 11 (11v11), asi que un
   * brazo fijo pasa de alargar un 70% al portero a TRIPLICARLO segun el modo.
   * Medido con ese valor: 5v5 se quedo en 1,0 goles por partido (venia de 3,4)
   * y 11v11 en 0,3 — el portero era un muro.
   */
  ARM_SCALE: 0.9,
  /**
   * Grosor de la MANO al final del brazo, en multiplos del radio del balon.
   *
   * Sin esto el brazo era una capsula del grosor del cuerpo: no un brazo, una
   * pared. Medido en el banco de tiro, tapaba +-97 u desde donde se lanzaba,
   * y la media boca del 5v5 son 95: cubria la porteria entera y no entraba
   * nada. El grosor se interpola del cuerpo a la mano a lo largo del brazo,
   * asi que estirarse llega mas lejos pero por un hilo — que es justo la
   * diferencia entre sacarla con la punta de los dedos y taparlo todo.
   */
  HAND_SCALE: 1.1,
  /**
   * Segundos en el suelo tras la estirada. Es el precio: sin esto el portero
   * seria un muro que se relanza cada tick y el rechace no valdria nada.
   */
  DIVE_COOLDOWN: 0.62,
  /**
   * Solo se lanza si el balon va a cruzar MAS LEJOS de lo que alcanza estando
   * de pie. Si llega andando, no hay estirada: un portero que se tira a todo
   * se ve ridiculo y ademas se queda en el suelo cuando llega el rechace.
   */
  DIVE_TRIGGER_GAP: 20,
  /**
   * Distancia maxima del balon al portero para comprometerse.
   *
   * Es el numero que decide si la estirada se puede batir. Lanzarse pronto
   * (600+) es leer el disparo casi desde el pie del rematador y no fallar
   * nunca; lanzarse tarde obliga a acertar el lado con poca informacion, que
   * es justo lo que hace que un paradon sea un paradon y un fallo un fallo.
   */
  DIVE_MAX_BALL_DIST: 300,

  /**
   * Error de lectura al elegir el lado, en unidades sobre el punto de cruce.
   *
   * Es lo que hace que la estirada sea JUSTA. Con lectura perfecta, un salto
   * largo tapa casi toda la boca y el disparo deja de decidir nada: medido en
   * el banco de tiro, el 5v5 pasaba del 20,6% al 10,2% de conversion a puerta,
   * o sea que el portero se comia la mitad de los goles del juego el solo.
   *
   * Con error, el portero se compromete y a veces se compromete MAL — y un
   * portero que ha volado al palo equivocado es un gol, no una injusticia. Se
   * sortea UNA vez al lanzarse, nunca por tick: sortearlo cada tick equivale a
   * quedarse con la tirada mas favorable y el error se evapora.
   */
  DIVE_READ_ERROR: 52,

  /**
   * Cuanto del rechace sale HACIA FUERA en vez de rebotar de vuelta al area.
   * 0 = como antes (rebote por la normal, muchas veces a los pies del que ha
   * disparado); 1 = todo a cordoba. El punto medio es lo que hace que un
   * paradon se lea como paradon y no como un regalo.
   */
  DEFLECT_WIDE: 0.5,
} as const;

/**
 * Fuera de juego, con su revision de VAR.
 *
 * La pausa se reparte en tres tramos, y el orden es el de una retransmision de
 * verdad: primero el aviso en vivo, luego la repeticion, y al final el
 * congelado con la linea trazada. El congelado va el ULTIMO a proposito — es la
 * imagen que decide, y la que tiene que quedarse en la retina antes de reanudar.
 */
export const OFFSIDE = {
  /** Holgura en unidades mundo (equivalente a "en linea"). */
  TOLERANCE: 6,
  /** Tras este tiempo suelto, el pase ya no puede generar fuera de juego. */
  PASS_WINDOW: 2.8,

  /** 1) Aviso en vivo: se para la jugada y sale el cartel. */
  WHISTLE: 0.85,
  /** 2) Repeticion de la jugada, con barras de cine como en el gol. */
  VAR_REPLAY: 2.4,
  /** 3) Congelado en el instante del pase, con la linea y las marcas. */
  VAR_FREEZE: 1.9,
  /**
   * Pausa total. Es la suma de los tres tramos: si dejan de cuadrar, un tramo
   * se come a otro y la revision se corta por la mitad.
   */
  PAUSE: 0.85 + 2.4 + 1.9,

  /**
   * A que distancia se retiran los rivales en el saque (u).
   *
   * Es la barrera de un libre indirecto llevada a esta escala. Sin ella el
   * saque era una trampa: el balon aparecia a los pies de un defensa con el
   * delantero que acababa de estar en fuera de juego pegado a la espalda, asi
   * que castigar al atacante le devolvia el balon a los dos segundos.
   */
  WALL_DISTANCE: 165,
  /** Aire desde la banda al colocar el balon (u). */
  SPOT_MARGIN: 70,
  /**
   * Aire EXTRA por delante del area propia al colocar el balon (u).
   *
   * El fuera de juego se pita donde estaba el atacante, y eso suele ser dentro
   * del area. Sacar desde ahi es lo mas peligroso que hay: un despeje corto y
   * el rival remata a puerta vacia. La regla real dice lo mismo (un indirecto
   * dentro del area pequeña se saca desde la linea del area), y aqui se lleva
   * hasta el borde del area grande.
   */
  SPOT_BOX_CLEARANCE: 40,
  /** Protege al que saca de que le roben en el mismo instante (s). */
  RESTART_LOCKOUT: 0.9,
} as const;

/** Limites de la duracion elegible. Un minuto no da para nada; media hora sobra. */
export const MATCH_MINUTES_MIN = 1;
export const MATCH_MINUTES_MAX = 30;
/** Lo que dura un partido si nadie toca nada. */
export const MATCH_DEFAULT_SECONDS = 300;
/** Cuantos tiempos se pueden pedir. Cuatro son los cuartos del baloncesto. */
export const MATCH_PERIODS_MIN = 1;
export const MATCH_PERIODS_MAX = 4;

/** Que pasa si el tiempo se acaba en empate. */
export type MatchDrawRule = "golden" | "draw";

/**
 * El formato del partido, elegible entero.
 *
 * Antes solo habia una duracion, un unico tiempo y el gol de oro OBLIGATORIO:
 * un partido no podia acabar en empate ni aunque se quisiera. Son tres cosas
 * distintas y ahora se eligen las tres.
 *
 * Todo esto es ESTADO DE LA SIMULACION —de aqui sale `timeRemaining` y de aqui
 * depende si el partido termina o sigue— asi que en red lo dicta la SALA, no la
 * preferencia de cada uno. Ver `resetMatchFormat`.
 */
export interface MatchFormat {
  /** Cuantos tiempos se juegan. */
  periods: number;
  /** Minutos de CADA tiempo. El total es `periods * minutes`. */
  minutes: number;
  /** Empate al final: se acepta, o se juega gol de oro. */
  onDraw: MatchDrawRule;
}

export const DEFAULT_MATCH_FORMAT: MatchFormat = {
  periods: 1,
  minutes: MATCH_DEFAULT_SECONDS / 60,
  onDraw: "golden",
};

export const MATCH = {
  /** Duracion del partido en segundos. */
  DURATION: 300,
  /**
   * Descanso entre tiempos, en segundos.
   *
   * Corto a proposito: es una pausa para respirar y ver el marcador, no un
   * intermedio. Lo bastante para que se note que ha acabado un tiempo, lo
   * bastante poco para que nadie suelte el mando.
   */
  HALF_TIME: 4,
  /**
   * Zoom + banner ¡GOLAZO! antes de la repetición.
   *
   * Era 1.35 s: el gol se cortaba a repetición casi en el mismo instante de
   * marcar, y la parte que se disfruta —ver la red moverse y el marcador
   * cambiar— no llegaba a existir. El aire de aqui es lo que convierte un
   * evento en una celebracion.
   */
  GOAL_CELEBRATION: 2.9,
  /** Segundos de juego que abarca el clip de repetición. */
  REPLAY_CLIP: 2.5,
  /**
   * Tiempo en pantalla para reproducir el clip.
   *
   * Subido de 4 a 5,2: el clip acababa justo cuando el balon entraba, y el
   * momento que se quiere ver —la red hundiendose— se cortaba en el fotograma
   * en que empezaba. Lo que se alarga es el final, no el principio: ver la
   * jugada mas lenta no la mejora, ver el gol mas rato si.
   *
   * Y bajado a 4,2 el 2026-09-28 por feedback de jugadores ("un poco mas
   * rapida"): con 5,2 la jugada se veia al 73 % de su velocidad y el gol
   * costaba 11 s de pausa. Ahora va al 90 % y la camara lenta del final
   * (`REPLAY_SLOWMO_TAIL`) casi no cambia: 1,4 s frente a 1,8.
   */
  REPLAY_PLAYBACK: 4.2,
  /**
   * Cuenta atras antes de reanudar tras un gol.
   *
   * Va DESPUES de la repeticion y es el ultimo tramo de `goalPause`. Antes el
   * juego volvia de golpe: se acababa el clip y el balon ya estaba rodando, asi
   * que el primer segundo de la reanudacion se jugaba sin haberse enterado de
   * que habia empezado. Con la cuenta, el saque se coloca ANTES de que corra el
   * reloj y todo el mundo ve la misma imagen: donde esta cada uno, y cuando.
   *
   * 3,3 s da tres numeros con aire: 3-2-1 a un segundo cada uno, mas la cola
   * del ultimo para que no desaparezca en el mismo fotograma en que se juega.
   */
  RESTART_COUNTDOWN: 3.3,
  /**
   * Fraccion final del clip que se reproduce a camara lenta.
   *
   * El balon ya esta dentro y la red trabajando: ahi es donde el tiempo extra
   * vale algo. Antes de eso, la jugada va a su ritmo.
   */
  REPLAY_SLOWMO_TAIL: 0.34,
  /** @deprecated Usar REPLAY_CLIP. */
  REPLAY_DURATION: 2.5,
  /** @deprecated Usar GOAL_CELEBRATION + REPLAY_PLAYBACK. */
  GOAL_PAUSE: 5.35,
} as const;

export const COLORS = {
  BACKGROUND: 0x14231a,
  /** Franja en sombra (verde pino frio). */
  GRASS_A: 0x142f24,
  /** Franja iluminada por focos (esmeralda calido). */
  GRASS_B: 0x2a6848,
  /** Brillo dorado-verde en el pico de la franja iluminada. */
  GRASS_WARM: 0x3d9460,
  LINE: 0xf2fff5,
  GOAL_FRAME: 0xf2fff5,
  GOAL_NET: 0x0d160f,
  HOME: 0xe5484d,
  HOME_ACCENT: 0xff8a8e,
  AWAY: 0x3e63dd,
  AWAY_ACCENT: 0x8fa8ff,
  CONTROL_RING: 0xffd76e,
  HUD_TEXT: 0xf2fff5,
  AIM: 0xffe58a,
  CHARGE: 0xffb03a,
  BALL: 0xfdfbf2,
  BALL_SHADE: 0x1b2419,
} as const;

/** Franjas de cesped; numero impar da simetria en el centro. */
export const GRASS_STRIPES = 9;

/**
 * Cambia lo que dura un partido.
 *
 * `MATCH.DURATION` lo lee la SIMULACION (`timeRemaining` arranca de ahi), asi
 * que forma parte del estado del lockstep: dos clientes con duraciones
 * distintas terminarian el partido en ticks distintos. Por eso solo se toca en
 * partido local, y el online se resetea antes de empezar — el mismo cuidado que
 * ya exige el plan de equipo.
 *
 * Se muta en sitio, igual que hace la jornada semanal (`applyWeeklyRule`), que
 * es el precedente que ya existia para esto.
 */
export function setMatchMinutes(minutes: number): void {
  const m = Math.min(MATCH_MINUTES_MAX, Math.max(MATCH_MINUTES_MIN, Math.round(minutes)));
  (MATCH as { DURATION: number }).DURATION = m * 60;
}

/** Vuelve a la duracion de fabrica. Se llama antes de un partido online. */
export function resetMatchDuration(): void {
  (MATCH as { DURATION: number }).DURATION = MATCH_DEFAULT_SECONDS;
}

/**
 * El formato vivo. Lo lee la simulacion al crearse y al acabar cada tiempo.
 *
 * Mutable en sitio, como `TEAM_INSTRUCTIONS`: hay un solo partido a la vez y
 * media docena de sitios lo consultan.
 */
export const MATCH_FORMAT: MatchFormat = { ...DEFAULT_MATCH_FORMAT };

/** Deja el formato en algo valido. Devuelve lo que ha quedado. */
export function setMatchFormat(next: Partial<MatchFormat>): MatchFormat {
  if (next.periods !== undefined) {
    MATCH_FORMAT.periods = Math.min(
      MATCH_PERIODS_MAX,
      Math.max(MATCH_PERIODS_MIN, Math.round(next.periods)),
    );
  }
  if (next.minutes !== undefined) {
    MATCH_FORMAT.minutes = Math.min(
      MATCH_MINUTES_MAX,
      Math.max(MATCH_MINUTES_MIN, Math.round(next.minutes)),
    );
    setMatchMinutes(MATCH_FORMAT.minutes);
  }
  if (next.onDraw !== undefined) MATCH_FORMAT.onDraw = next.onDraw;
  return MATCH_FORMAT;
}

/**
 * Formato de fabrica. Se llama antes de un partido ONLINE.
 *
 * Mismo cuidado que con el plan de equipo: si cada cliente entrara con el
 * formato que tuviera guardado de su ultima partida local, uno jugaria dos
 * tiempos y el otro uno, y el partido acabaria en ticks distintos.
 */
export function resetMatchFormat(): void {
  Object.assign(MATCH_FORMAT, DEFAULT_MATCH_FORMAT);
  resetMatchDuration();
}

/** ¿Es una regla de empate valida? Para validar lo que llega por la red. */
export function isDrawRule(v: unknown): v is MatchDrawRule {
  return v === "golden" || v === "draw";
}
